/* SPECULUM — Screens
   Bench · Intake · Examination · Findings · Verdict · Archive */

const { useState, useEffect, useRef, useMemo } = React;

/* Payment providers. PayPal is hidden until a bank account is connected and
   live credentials are set — flip to true to re-enable the PayPal option.
   (All PayPal code/functions stay intact; this only controls the UI.) */
const PAYPAL_ENABLED = false;

/* ── Plain-language glossary for section labels ──
   Shown as a hover/tap tooltip so new users understand each term. */
const SECTION_GLOSSARY = {
  "Specimen": "The original text you submitted, shown exactly as received and unedited. This is the thing being examined.",
  "Synopsis": "A one-line plain summary of what the text is really doing beneath the surface — its underlying move, not just its topic.",
  "Most Operative Phrase": "The single line in the text doing the heaviest persuasive work — the phrase the whole piece pivots on.",
  "Symbol Inventory": "The key symbols or images the text leans on, and what each one is quietly making you feel or assume.",
  "Register": "The 'voice' the text borrows — e.g. scholarly, scriptural, intimate — and what that borrowed tone is used to achieve.",
  "Narrative Frame": "The story the text invites you into, and the role it casts you in as the reader.",
  "Affective Induction": "The emotions the text is engineered to trigger, ranked by how strongly it pushes each one.",
  "Ritual Mechanics": "The repeatable persuasion techniques at work — the 'machinery' of how it moves you.",
  "Rhetorical Devices": "Named rhetorical techniques found in the text (e.g. repetition, rhetorical questions), with the exact words that use them.",
  "Strategic Omissions": "What the text conspicuously leaves out — gaps that are doing work by being absent.",
  "Power Dynamics": "Who is speaking, who is being addressed, and the imbalance of power between them.",
};

/* A section label with an on-hover / on-tap plain-language explanation. */
function SectionName({ children }) {
  const key = typeof children === "string" ? children : "";
  const note = SECTION_GLOSSARY[key];
  if (!note) return <span className="name">{children}</span>;
  return (
    <span className="name name--explain" tabIndex={0}>
      {children}
      <span className="name-info" aria-hidden="true">?</span>
      <span className="name-tooltip" role="tooltip">{note}</span>
    </span>
  );
}

/* Build an Intake preload object that re-runs an existing examination,
   carrying lineage so the new reading can be measured against the old. */
function reexamPreload(data) {
  if (!data) return null;
  const prevLin = data.report?._lineage;
  return {
    text:   data.input?.text || "",
    medium: data.input?.medium || "ad",
    stance: data.input?.stance || "study",
    tags:   data.input?.tags || ["public-domain"],
    lineage: {
      origin_id: prevLin?.origin_id || data.id,
      prev_id:   data.id,
      version:   prevLin?.version || 1,
      prevDrift: data.report?.drift_quotient,
      prevGrade: data.grade,
    },
  };
}

/* ============================================================
   SHARE CARD OVERLAY
   ============================================================ */
function ShareCard({ data, onClose }) {
  const [copied, setCopied] = React.useState(false);
  if (!data) return null;
  const d = data.report;

  // Grade → drift color
  const driftColor = parseFloat(d.drift_quotient) < 0.2 ? "var(--signal)"
    : parseFloat(d.drift_quotient) < 0.5 ? "var(--accent)"
    : "var(--warn)";

  // Scorecard mini pills — use PRINCIPLES keys if available
  const principleKeys = ["transparency","consent","harm_reduction","power_awareness","empirical","cultural"];
  const principleNums = ["§I","§II","§III","§IV","§V","§VI"];

  function copyText() {
    const text = `SPECVLVM · Auditus\nExamination: ${d.title}\nGrade: ${d.grade}\nVerdict: ${d.verdict_headline}\n\nDrift Quotient: ${d.drift_quotient ?? "—"}\n\n"${d.verdict_blurb}"\n\nOrdo Semeion · auditus.ra999.info`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className={`share-overlay open`} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="share-modal">
        <div className="share-modal-header">
          <span className="share-modal-title">EXAMINATION FINDINGS · SHARE</span>
          <button className="share-modal-close" onClick={onClose}>✕ CLOSE</button>
        </div>

        {/* THE CARD */}
        <div className="share-card" id="share-card-el">
          <div className="share-card-header">
            <div className="share-card-header-left">
              <svg width="18" height="18" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="14.5" stroke="var(--accent)" strokeWidth="1" opacity="0.6"/>
                <circle cx="16" cy="16" r="9" stroke="var(--accent)" strokeWidth="0.7" opacity="0.4"/>
                {[0,60,120,180,240,300].map((deg,i)=>{const r=9,rad=deg*Math.PI/180;return<circle key={i} cx={16+Math.cos(rad)*r} cy={16+Math.sin(rad)*r} r="1.1" fill="var(--accent)" opacity="0.6"/>;})}
                <path d="M16 10.5 C18 13 19.5 15 19.5 17 C19.5 19.5 17.9 21.5 16 21.5 C14.1 21.5 12.5 19.5 12.5 17 C12.5 15 14 13 16 10.5Z" fill="var(--ink)" opacity="0.9"/>
              </svg>
              <span className="share-card-logo">SPECVLVM <em>·</em> Auditus</span>
            </div>
            <span className="share-card-meta">FIELD INSTRUMENT №01 · ORDO SEMEION</span>
          </div>

          <div className="share-card-body">
            <div className="share-card-specimen">
              {d.title}
            </div>

            <div className="share-card-grade-row">
              <div className="share-card-grade">{d.grade}</div>
              <div className="share-card-verdict">{d.verdict_headline}<br/>
                <span style={{fontSize:12,color:"var(--ink-faint)"}}>{(d.verdict_blurb||"").slice(0,120)}{d.verdict_blurb?.length > 120 ? "…" : ""}</span>
              </div>
              <div className="share-card-drift">
                <span className="share-card-drift-num" style={{color:driftColor}}>
                  {d.drift_quotient ?? d.asymmetry ?? "—"}
                </span>
                <span className="share-card-drift-label">DRIFT §VI</span>
              </div>
            </div>

            {/* Scorecard pills */}
            <div className="share-card-principles">
              {principleKeys.map((k,i) => {
                const score = d.scorecard?.[k]?.score ?? "—";
                const scoreNum = parseFloat(score);
                const col = scoreNum >= 7 ? "var(--signal)" : scoreNum >= 4 ? "var(--accent)" : "var(--warn)";
                return (
                  <div className="share-card-principle" key={k}>
                    <span className="share-card-p-num">{principleNums[i]}</span>
                    <span className="share-card-p-score" style={{color:col}}>{score}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="share-card-footer">
            <span className="share-card-url">auditus.ra999.info</span>
            <span className="share-card-notice">NO SPECIMENS STORED · SESSION ONLY</span>
          </div>
        </div>

        <div className="share-modal-actions">
          <button className={`share-copy-btn ${copied?"copied":""}`} onClick={copyText}>
            {copied ? "✓ COPIED" : "COPY FINDINGS TEXT →"}
          </button>
          <button className="share-dismiss-btn" onClick={onClose}>DISMISS</button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   BENCH (home / splash)
   ============================================================ */
/* Latin antiphons — short phrases under each section */
const ANTIPHONS = {
  synopsis:   "— quod specimen facit —",
  symbols:    "— signa operativa —",
  narrative:  "— fabula invitans —",
  states:     "— affectus inductus —",
  mechanics:  "— mechanica ritualis —",
  power:      "— asymmetria —",
  specimen:   "— corpus ipsum —",
  operative:  "— verbum operativum —",
};

// Master sigil that adapts to palette — winged in seraphic, geometric otherwise
function AdaptiveSigil(props) {
  const palette = (typeof document !== "undefined" && document.body.dataset.palette) || "ink";
  return palette === "seraphic"
    ? <WingedSigil {...props} />
    : <MasterSigil {...props} />;
}

// Highlight a phrase within text body — returns line-by-line segments
function renderSpecimenLines(text, highlight) {
  const lines = (text || "").split(/\n/);
  const hl = (highlight || "").trim();
  return lines.map((ln, i) => {
    if (!hl || !ln.toLowerCase().includes(hl.toLowerCase())) {
      return <div key={i} className="line">{ln || "\u00A0"}</div>;
    }
    const idx = ln.toLowerCase().indexOf(hl.toLowerCase());
    return (
      <div key={i} className="line">
        {ln.slice(0, idx)}
        <span className="highlighted">{ln.slice(idx, idx + hl.length)}</span>
        {ln.slice(idx + hl.length)}
      </div>
    );
  });
}

/* ── Animated instrument header ── */
function InstrumentHeader({ specimenCount }) {
  const [time, setTime] = React.useState(new Date());
  const [tick, setTick] = React.useState(0);
  const STATUS_CYCLE = [
    "AWAITING SPECIMEN",
    "APPARATUS · IDLE",
    "CHARTER §§I–VI · ACTIVE",
    "SIGNAL INTEGRITY · NOMINAL",
    "ORDO·SEMEION · MMXXVI",
    "NO RECORD KEPT · SESSION ONLY",
  ];

  React.useEffect(() => {
    const t = setInterval(() => {
      setTime(new Date());
      setTick(n => n + 1);
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const statusText = STATUS_CYCLE[Math.floor(tick / 4) % STATUS_CYCLE.length];
  const timeStr = time.toLocaleTimeString("en-GB", { hour:"2-digit", minute:"2-digit", second:"2-digit" });

  return (
    <div className="instrument-header">
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <span className="mono-sm" style={{ color: "var(--ink-dim)" }}>Apparatus</span>
        <span className="mono-sm" style={{ color: "var(--accent)", opacity:0.7 }}>·</span>
        <span className="mono-sm" style={{
          color: "var(--accent)",
          animation: "statusFade 0.4s ease",
          animationFillMode: "both",
          key: statusText,
        }}>
          {statusText}
        </span>
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:14 }}>
        <span className="mono-sm" style={{ color: "var(--ink-faint)", fontVariantNumeric:"tabular-nums" }}>
          {timeStr}
        </span>
        <span className="mono-sm" style={{ color: "var(--ink-faint)" }}>
          {String(specimenCount).padStart(3,"0")} specimens
        </span>
      </div>
    </div>
  );
}

function Bench({ go, archive, signedIn, credits = 0, onUpgrade }) {
  const isExaminer = signedIn && credits > 0;
  const recent = archive.slice(0, 4);
  const sessionHistory = archive.slice(0, 3);
  const SAMPLES_BENCH = [
    { label: "Manifesto", key: "manifesto" },
    { label: "Ad Copy", key: "ad" },
    { label: "Speech", key: "speech" },
  ];

  // Keyboard shortcuts
  React.useEffect(() => {
    function onKey(e) {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (e.key === "e" || e.key === "E") go("intake");
      if (e.key === "a" || e.key === "A") go("archive");
      if (e.key === "c" || e.key === "C") go("lore");
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go]);

  // Compute stats
  const realExams = archive.filter(a => !a.seeded);
  const meanDrift = realExams.length
    ? (realExams.reduce((s,a) => s + parseFloat(a.report?.drift_quotient || 0), 0) / realExams.length).toFixed(2)
    : "—";
  const grades = realExams.map(a => a.grade).filter(Boolean);
  const topGrade = grades.length ? [...grades].sort()[0] : "—";

  // Streak dots (last 7 days)
  const streakDots = Array.from({length:7}).map((_,i) => {
    const d = new Date(); d.setDate(d.getDate() - (6-i));
    const dateStr = d.toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"2-digit"}).toUpperCase();
    const hasExam = realExams.some(a => a.ts?.includes(dateStr.slice(0,6)));
    return { active: hasExam, recent: i === 6 };
  });
  return (
    <div className="bench">
      <div className="bench-left">
        {/* Examiner status */}
        {signedIn ? (
          <div className={`examiner-status-bar ${credits > 0 ? "" : "examiner-status-bar--free"}`} onClick={credits > 0 ? undefined : onUpgrade}>
            <span className={`examiner-status-dot ${credits > 0 ? "" : "examiner-status-dot--free"}`}/>
            <span className="mono-sm" style={{color: credits > 0 ? "var(--signal)" : "var(--ink-faint)"}}>
              EXAMINER · {credits} CREDIT{credits === 1 ? "" : "S"} REMAINING
            </span>
            <span className="mono-sm" style={{color:"var(--accent)", marginLeft:"auto", cursor:"pointer"}} onClick={onUpgrade}>
              {credits > 0 ? "Add credits →" : "Acquire credits →"}
            </span>
          </div>
        ) : (
          <div className="examiner-status-bar examiner-status-bar--free" onClick={onUpgrade}>
            <span className="examiner-status-dot examiner-status-dot--free"/>
            <span className="mono-sm" style={{color:"var(--ink-faint)"}}>PUBLIC SIGNAL KEY · LIMITED FREE EXAMINATIONS</span>
            <span className="mono-sm" style={{color:"var(--accent)", marginLeft:"auto"}}>Get credits →</span>
          </div>
        )}
        <div className="label" style={{ marginBottom: 28 }}>· Field Instrument № 01 · Issued by the Order ·</div>
        <h1 className="preamble">
          A bench for the <em>examination</em> of meaning under deployment.
        </h1>
        <p style={{ maxWidth: "48ch", color: "var(--ink-dim)", fontSize: 17, lineHeight: 1.55, marginBottom: 0 }}>
          Place a specimen — advertisement, address, manifesto, packaging — on the bench. The instrument disassembles its symbolic apparatus and reports against the six principles. The findings are yours; nothing is retained unless you choose to save it to your archive.
        </p>

        {/* Stats row — examiner only */}
        {isExaminer && realExams.length > 0 && (
          <div className="bench-stats-row">
            <div className="bench-stat">
              <span className="bench-stat-val">{String(realExams.length).padStart(3,"0")}</span>
              <span className="bench-stat-label">Examinations</span>
            </div>
            <div className="bench-stat">
              <span className="bench-stat-val gold">{meanDrift}</span>
              <span className="bench-stat-label">Mean Drift §VI</span>
            </div>
            <div className="bench-stat">
              <span className="bench-stat-val">{topGrade}</span>
              <span className="bench-stat-label">Best Grade</span>
            </div>
          </div>
        )}
        <div className="bench-sample-row">
          <span className="bench-sample-label">Try a sample:</span>
          {SAMPLES_BENCH.map(s => (
            <button key={s.key} className="bench-sample-btn" onClick={() => go("intake", { preload: s.key })}>
              {s.label}
            </button>
          ))}
        </div>
        <dl className="bench-meta" style={{ marginTop: 24 }}>
          <dt>Instrument</dt><dd>Speculum · Rev. III</dd>
          <dt>Discipline</dt><dd>Applied Semiotics</dd>
          <dt>Calibration</dt><dd>22 May 2026, 09:14 GMT</dd>
          <dt>Ethics Charter</dt><dd>Ordo Semeion, §§I–VI</dd>
        </dl>

        <div className="action-row">
          <button className="cta-primary" onClick={() => go("intake")}>
            Begin Examination <span className="arr">→</span>
          </button>
          <button className="cta-ghost" onClick={() => go("archive")}>View Archive</button>
        </div>

        {/* Keyboard shortcuts */}
        <div className="bench-shortcuts">
          <div className="bench-shortcut">
            <span className="bench-shortcut-key">E</span>
            <span className="bench-shortcut-label">Examine</span>
          </div>
          <div className="bench-shortcut">
            <span className="bench-shortcut-key">A</span>
            <span className="bench-shortcut-label">Archive</span>
          </div>
          <div className="bench-shortcut">
            <span className="bench-shortcut-key">C</span>
            <span className="bench-shortcut-label">Codex</span>
          </div>
        </div>

        <div className="bench-history">
          <div className="bench-history-head">
            <span className="bench-history-label">{isExaminer ? "Recent Examinations" : "This Session"}</span>
            {sessionHistory.length > 0 && (
              <button className="bench-sample-btn" onClick={() => go("archive")} style={{fontSize:"8.5px"}}>All →</button>
            )}
          </div>
          {sessionHistory.length === 0 ? (
            <div className="bench-history-items">
              <div className="bench-history-empty">No examinations yet this session.</div>
            </div>
          ) : (
            <div className="bench-history-items">
              {sessionHistory.map(a => (
                <div key={a.id} className="bench-history-item" onClick={() => go("findings", a)}>
                  <span className="bench-history-title">{a.title}</span>
                  <span className="bench-history-ts">{a.ts}</span>
                  <span className="bench-history-grade" style={{color: a.grade?.charAt(0)==="A"?"var(--signal)":a.grade?.charAt(0)==="B"?"var(--accent)":a.grade?.charAt(0)==="F"?"var(--warn)":"var(--ink-dim)", borderColor:"currentColor"}}>{a.grade}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bench-seal">
          <BrandMark size={48} />
          <div className="seal-text">
            ORDO·SEMEION·MMXXVI<br />
            Instrumenta · Auditus · Disciplina<br />
            <span style={{ color: "var(--ink-dim)" }}>“No magic; only mechanism, made plain.”</span>
          </div>
        </div>
      </div>

      <div className="bench-right">
        <div className="instrument">
          <InstrumentHeader specimenCount={archive.length} />
          <div className="instrument-body">
            <div className="sigil-premium">
              <AdaptiveSigil size={420} spin={true} />
            </div>
          </div>
          <div className="instrument-foot">
            <div className="gauge">
              <span className="gauge-label">Specimens</span>
              <span className="gauge-value">{String(archive.length).padStart(3, "0")}</span>
            </div>
            <div className="gauge">
              <span className="gauge-label">Mean Verdict</span>
              <span className="gauge-value" style={{ color: "var(--accent)" }}>{topGrade !== "—" ? topGrade : "—"}</span>
            </div>
            <div className="gauge">
              <span className="gauge-label">Charter</span>
              <span className="gauge-value">§I–VI</span>
            </div>
            <div className="gauge">
              <span className="gauge-label">Drift §VI</span>
              <span className="gauge-value" style={{ color: parseFloat(meanDrift) < 0.3 ? "var(--signal)" : parseFloat(meanDrift) < 0.6 ? "var(--accent)" : "var(--warn)" }}>
                {meanDrift}
              </span>
            </div>
          </div>
        </div>

        <div className="recent">
          <div className="recent-head">
            <span className="mono-sm">Recent Examinations</span>
            <button className="mono-sm" style={{ color: "var(--ink-faint)" }} onClick={() => go("archive")}>All →</button>
          </div>

          {/* DQ trend sparkline */}
          {recent.filter(a=>!a.seeded).length > 1 && (
            <div style={{padding:"10px 16px", borderBottom:"1px solid var(--rule-soft)", display:"flex", alignItems:"flex-end", gap:3, height:44}}>
              <span className="mono-sm" style={{color:"var(--ink-faint)",marginRight:8,flexShrink:0,alignSelf:"center"}}>DQ</span>
              {recent.filter(a=>!a.seeded).slice(0,8).reverse().map((a,i) => {
                const dq = parseFloat(a.report?.drift_quotient || 0);
                const h = Math.max(4, Math.round(dq * 28));
                const col = dq >= 0.7 ? "var(--warn)" : dq >= 0.4 ? "var(--accent)" : "var(--signal)";
                return (
                  <div key={a.id} title={`${a.title}: DQ ${dq.toFixed(2)}`}
                    style={{flex:1, height:`${h}px`, background:col, opacity:0.6, borderRadius:1, transition:"opacity 0.2s ease", cursor:"default"}}
                    onMouseEnter={e=>e.target.style.opacity=1}
                    onMouseLeave={e=>e.target.style.opacity=0.6}
                  />
                );
              })}
              <span className="mono-sm" style={{color:"var(--ink-faint)",marginLeft:4,alignSelf:"center",flexShrink:0}}>trend</span>
            </div>
          )}

          <ul>
            {recent.length === 0 && (
              <li style={{ gridTemplateColumns: "1fr" }}>
                <span className="empty-hint">No examinations on record. Begin one to populate the archive.</span>
              </li>
            )}
            {recent.map(a => {
              const gc = a.grade?.charAt(0) === "A" ? "var(--signal)"
                : a.grade?.charAt(0) === "B" ? "var(--accent)"
                : a.grade?.charAt(0) === "C" ? "var(--ink-dim)"
                : "var(--warn)";
              return (
                <li key={a.id} onClick={() => go("findings", a)}>
                  <span className="ts">{a.ts}</span>
                  <span className="title serif-italic">{a.title}</span>
                  <span className={`verdict v-${a.verdictClass}`} style={{color:gc, borderColor:gc}}>{a.grade}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   INTAKE
   ============================================================ */
const MEDIA = [
  { id: "ad", label: "Advertisement" },
  { id: "speech", label: "Address / Speech" },
  { id: "manifesto", label: "Manifesto" },
  { id: "brand", label: "Brand Statement" },
  { id: "editorial", label: "Editorial" },
  { id: "social", label: "Social Post" },
];
const STANCES = [
  { id: "study", label: "Study" },
  { id: "publish", label: "Publish" },
  { id: "defend", label: "Defend Against" },
];

const SAMPLES = {
  manifesto: `Friends — the great work of our generation is not behind us. It is at this very door. We have been told that the old fires are extinguished, that the symbols are dead, that meaning itself has been audited away. We say: enough.\n\nWe are the Order of the Unforbidden Sciences. We restore what was hidden in plain sight: that meaning is made, not given; that symbols still work; that ritual still binds; that the future will be drawn by those who remember how to draw it.\n\nJoin us. Wear the mark. Speak the words. Be remade.`,
  ad: `INTRODUCING ECHELON.\nThe watch worn by people who don't need to tell you what they do.\n\nForged in Geneva. Tested at 6,000m. Yours in three finishes.\nLimited to 1,728 pieces, worldwide.\n\nBecome the question.`,
  speech: `My fellow citizens — when our grandfathers stood on these same stones, they were not asking permission. They were not asking forgiveness. They were taking back what was theirs. Tonight, we are not asking either. We are remembering. And remembering, we rise.`,
};

function Intake({ go, run, runBatch, preload, credits = 0, signedIn }) {
  // preload can be a sample-key string, or a re-examine object:
  // { text, medium, stance, tags, lineage }
  const isReexam = preload && typeof preload === "object";
  const [text, setText] = useState(
    isReexam ? (preload.text || "") : (preload ? (SAMPLES[preload] || "") : "")
  );
  const [medium, setMedium] = useState(
    isReexam ? (preload.medium || "ad")
    : preload === "speech" ? "speech" : preload === "manifesto" ? "manifesto" : "ad"
  );
  const [stance, setStance] = useState(isReexam ? (preload.stance || "study") : "study");
  const [tags, setTags] = useState(new Set(
    isReexam && Array.isArray(preload.tags) && preload.tags.length ? preload.tags : ["public-domain"]
  ));
  const [consent, setConsent] = useState({ research: true, archive: false, party: false });
  const lineage = isReexam ? preload.lineage : null;
  const [batchMode, setBatchMode] = useState(false);
  const charCount = text.length;
  const wordCount = useMemo(() => text.trim().split(/\s+/).filter(Boolean).length, [text]);

  // Batch: an explicit list of specimen fields the user adds/removes.
  const [batchTexts, setBatchTexts] = useState(["", ""]);
  const updateBatch = (i, v) => setBatchTexts(prev => prev.map((t, idx) => idx === i ? v : t));
  const addBatchField = () => setBatchTexts(prev => prev.length < 12 ? [...prev, ""] : prev);
  const removeBatchField = (i) => setBatchTexts(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);
  const validBatch = batchTexts.map(t => t.trim()).filter(t => t.length > 40);
  const batchCount = validBatch.length;

  const toggleTag = (t) => {
    const s = new Set(tags);
    s.has(t) ? s.delete(t) : s.add(t);
    setTags(s);
  };

  const canSubmit = text.trim().length > 40 && consent.research;

  return (
    <div className="intake">
      <aside className="intake-rail">
        <div>
          <div className="label" style={{ marginBottom: 14 }}>Specimen</div>
          <h2 style={{ fontSize: 28, fontStyle: "italic", lineHeight: 1.1, marginBottom: 8 }}>Preparation</h2>
          <p style={{ fontSize: 14, color: "var(--ink-dim)", lineHeight: 1.55 }}>
            Declare the medium, your stance, and the conditions of consent. The instrument refuses to operate without them.
          </p>
        </div>

        <div className="field">
          <span className="label">Medium</span>
          <div className="chips">
            {MEDIA.map(m => (
              <button key={m.id} className={`chip ${medium === m.id ? "on" : ""}`} onClick={() => setMedium(m.id)}>
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <span className="label">Auditor's Stance</span>
          <div className="seg">
            {STANCES.map(s => (
              <button key={s.id} className={stance === s.id ? "on" : ""} onClick={() => setStance(s.id)}>
                {s.label}
              </button>
            ))}
          </div>
          <span className="mono-sm" style={{ color: "var(--ink-faint)" }}>
            {stance === "study" && "// for internal practice only"}
            {stance === "publish" && "// findings may be circulated"}
            {stance === "defend" && "// to immunize against deployment"}
          </span>
        </div>

        <div className="field">
          <span className="label">Provenance Tags</span>
          <div className="chips">
            {["public-domain", "consented", "third-party", "unknown", "self-authored"].map(t => (
              <button key={t} className={`chip ${tags.has(t) ? "on" : ""}`} onClick={() => toggleTag(t)}>
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <span className="label">Charter Acknowledgement</span>
          <div className="consent-block">
            {[
              { k: "research", t: "I am operating under the Charter of the Order.", s: "Required to begin examination." },
              { k: "archive", t: "Examinations I save are stored to my account.", s: "Unsaved audits are kept only for this session; saving an examination stores its text and findings to your account." },
              { k: "party", t: "If a third party is named, their interests are weighed.", s: "Power-awareness clause §IV." },
            ].map(({k,t,s}) => (
              <label key={k} className={`consent-row ${consent[k] ? "on" : ""}`}
                     onClick={() => setConsent({...consent, [k]: !consent[k]})}>
                <span className="box">{consent[k] ? "✓" : ""}</span>
                <span className="lbl">{t}<small>{s}</small></span>
              </label>
            ))}
          </div>
        </div>
      </aside>

      <main className="intake-main">
        <div className="label" style={{ marginBottom: 10 }}>§ 02 · Examination · Specimen Intake</div>
        {lineage && (
          <div className="reexam-banner">
            <span className="reexam-banner-mark">⟲</span>
            <div>
              <div className="reexam-banner-title">Re-examination · revision {(lineage.version || 1) + 1}</div>
              <div className="reexam-banner-sub">
                Edit the specimen below and re-run. The instrument will measure how its drift and grade
                have moved from the prior reading{lineage.prevDrift != null ? ` (was DQ ${Number(lineage.prevDrift).toFixed(2)} · ${lineage.prevGrade || "—"})` : ""}.
              </div>
            </div>
          </div>
        )}
        <h1 style={{ fontSize: 56, fontStyle: "italic", letterSpacing: "-0.02em", lineHeight: 1.0, marginBottom: 8 }}>
          {lineage ? "Revise the specimen." : "Place the specimen."}
        </h1>
        <p style={{ color: "var(--ink-dim)", fontSize: 17, maxWidth: "58ch", marginBottom: 28 }}>
          Paste the text below. The instrument operates on whole utterances — slogans, paragraphs, addresses. The longer the corpus, the finer the resolution; the shorter, the sharper the verdict.
        </p>

        {/* Batch toggle — re-examine and batch are mutually exclusive */}
        {!lineage && (
          <div className="batch-toggle-row">
            <button
              className={`chip ${batchMode ? "on" : ""}`}
              onClick={() => setBatchMode(m => !m)}>
              {batchMode ? "✓ Batch mode" : "⊞ Batch mode"}
            </button>
            <span className="batch-toggle-hint">
              {batchMode
                ? `One field per specimen — all share the medium, stance, and tags. ${batchCount} ready · ${batchCount} credit${batchCount===1?"":"s"}.`
                : "Examine several specimens in one run — one credit each."}
            </span>
          </div>
        )}

        {/* ── SINGLE specimen ── */}
        {!batchMode && (
          <div className="field" style={{ marginBottom: 20 }}>
            <div className="label">
              <span>Corpus · {MEDIA.find(m => m.id === medium).label}</span>
              <span style={{ color: "var(--ink-faint)" }}>{wordCount} words · {charCount} chars</span>
            </div>
            <textarea
              className="textarea"
              placeholder="Paste advertisement, speech, manifesto, or brand statement here…"
              value={text}
              onChange={e => setText(e.target.value)}
              spellCheck={false}
              maxLength={10000}
            />
            {charCount > 7000 && (
              <div style={{fontFamily:"var(--mono)",fontSize:"9px",letterSpacing:"0.1em",color:charCount>9000?"var(--warn)":"var(--accent)",marginTop:6,textTransform:"uppercase"}}>
                {charCount > 9000 ? `⚠ ${10000 - charCount} CHARS REMAINING — APPROACHING LIMIT` : `${10000 - charCount} CHARS REMAINING`}
              </div>
            )}
          </div>
        )}

        {/* ── BATCH specimens — one field each ── */}
        {batchMode && (
          <div className="batch-fields" style={{ marginBottom: 20 }}>
            {batchTexts.map((bt, i) => {
              const wc = bt.trim().split(/\s+/).filter(Boolean).length;
              const ok = bt.trim().length > 40;
              return (
                <div key={i} className="batch-field">
                  <div className="batch-field-head">
                    <span className="batch-field-num">Specimen {String(i + 1).padStart(2, "0")}</span>
                    <span className="batch-field-meta">
                      {bt.trim().length === 0 ? "empty"
                        : ok ? `${wc} words · ready`
                        : "too short (40+ chars)"}
                    </span>
                    {batchTexts.length > 1 && (
                      <button className="batch-field-remove" title="Remove" onClick={() => removeBatchField(i)}>✕</button>
                    )}
                  </div>
                  <textarea
                    className="textarea batch-textarea"
                    placeholder={`Paste specimen ${i + 1}…`}
                    value={bt}
                    onChange={e => updateBatch(i, e.target.value)}
                    spellCheck={false}
                    maxLength={10000}
                  />
                </div>
              );
            })}
            {batchTexts.length < 12 && (
              <button className="batch-add-btn" onClick={addBatchField}>+ Add specimen</button>
            )}
          </div>
        )}

        <div className="action-row" style={{ justifyContent: "space-between", width: "100%" }}>
          <div className="action-row" style={{ marginTop: 0, gap: 10 }}>
            {!batchMode && <span className="mono-sm" style={{ color: "var(--ink-faint)" }}>Try a sample:</span>}
            {!batchMode && Object.keys(SAMPLES).map(k => (
              <button key={k} className="chip" onClick={() => { setText(SAMPLES[k]); setMedium(k === "speech" ? "speech" : k === "manifesto" ? "manifesto" : "ad"); }}>
                {k}
              </button>
            ))}
          </div>
          {batchMode ? (
            <button
              className="cta-primary"
              disabled={!consent.research || batchCount < 1}
              style={{ opacity: (consent.research && batchCount >= 1) ? 1 : 0.35, cursor: (consent.research && batchCount >= 1) ? "pointer" : "not-allowed" }}
              onClick={() => consent.research && batchCount >= 1 && runBatch(
                validBatch.map(t => ({ text: t, medium, stance, tags: [...tags] }))
              )}
            >
              Examine {batchCount} Specimen{batchCount === 1 ? "" : "s"} <span className="arr">→</span>
            </button>
          ) : (
            <button
              className="cta-primary"
              disabled={!canSubmit}
              style={{ opacity: canSubmit ? 1 : 0.35, cursor: canSubmit ? "pointer" : "not-allowed" }}
              onClick={() => canSubmit && run({ text, medium, stance, tags: [...tags], lineage })}
            >
              Commence Examination <span className="arr">→</span>
            </button>
          )}
        </div>
      </main>
    </div>
  );
}

/* ============================================================
   EXAMINATION (loading)
   ============================================================ */
const STAGES = [
  { label: "Stripping rhetorical envelope",     principle: null },
  { label: "Indexing symbolic apparatus",        principle: "§I · Sign Function" },
  { label: "Tracing narrative invitations",      principle: "§II · Denotation / Connotation" },
  { label: "Measuring affective induction",      principle: "§III · Myth & Ideology" },
  { label: "Surveying ritual mechanics",         principle: "§IV · Codes & Conventions" },
  { label: "Profiling addressivity",             principle: "§V · Addressivity" },
  { label: "Calculating Drift Quotient",         principle: "§VI · Drift Quotient" },
  { label: "Assembling the verdict",             principle: null },
];

function Examination({ progress }) {
  const [particles, setParticles] = React.useState([]);

  // Spawn particles on stage advance
  React.useEffect(() => {
    const newP = Array.from({length: 6}).map((_, i) => ({
      id: Date.now() + i,
      x: 30 + Math.random() * 40,
      y: 30 + Math.random() * 40,
      delay: i * 0.1,
    }));
    setParticles(p => [...p.slice(-18), ...newP]);
  }, [progress]);

  const stageIdx = Math.min(progress, STAGES.length - 1);
  const currentStage = STAGES[stageIdx];
  const pct = Math.min(100, Math.round((progress / (STAGES.length - 1)) * 100));
  const stageNum = Math.min(progress + 1, STAGES.length);

  // Circular progress ring geometry
  const RING = 150;                 // viewBox size
  const R = 66;                     // radius
  const C = 2 * Math.PI * R;        // circumference
  const dash = C * (1 - pct / 100); // stroke offset

  return (
    <div className="examination">
      {/* Particle field */}
      <div className="exam-particles">
        {particles.map(p => (
          <div key={p.id} className="exam-particle" style={{
            left: `${p.x}%`, top: `${p.y}%`,
            animationDelay: `${p.delay}s`
          }}/>
        ))}
      </div>

      <div className="examination-core">
        {/* Sigil inside a circular progress ring */}
        <div className="exam-dial">
          {/* Orbital backdrop rings */}
          <div className="exam-orbit exam-orbit--1" />
          <div className="exam-orbit exam-orbit--2" />

          <svg className="exam-ring-svg" viewBox={`0 0 ${RING} ${RING}`}>
            <defs>
              <linearGradient id="examRingGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="var(--accent-deep)" />
                <stop offset="100%" stopColor="var(--accent)" />
              </linearGradient>
            </defs>
            {/* track */}
            <circle cx={RING/2} cy={RING/2} r={R} fill="none"
              stroke="var(--rule)" strokeWidth="1.5" opacity="0.5" />
            {/* tick marks every 12.5% (8 stages) */}
            {STAGES.map((_, i) => {
              const a = (i / STAGES.length) * 2 * Math.PI - Math.PI / 2;
              const r1 = R + 6, r2 = R + 10;
              const done = i <= progress;
              return <line key={i}
                x1={RING/2 + Math.cos(a)*r1} y1={RING/2 + Math.sin(a)*r1}
                x2={RING/2 + Math.cos(a)*r2} y2={RING/2 + Math.sin(a)*r2}
                stroke={done ? "var(--accent)" : "var(--rule)"} strokeWidth="1.5"
                opacity={done ? 0.9 : 0.4} />;
            })}
            {/* progress arc */}
            <circle cx={RING/2} cy={RING/2} r={R} fill="none"
              stroke="url(#examRingGrad)" strokeWidth="2.5" strokeLinecap="round"
              strokeDasharray={C} strokeDashoffset={dash}
              transform={`rotate(-90 ${RING/2} ${RING/2})`}
              style={{ transition: "stroke-dashoffset .6s ease" }} />
          </svg>

          {/* Sigil + breathing halo (animated via CSS, not JS re-renders) */}
          <div className="exam-sigil-wrap">
            <div className="exam-sigil-halo exam-sigil-halo--breathe"/>
            <AdaptiveSigil size={120} spin={true} />
          </div>

          {/* Percentage centred in the dial */}
          <div className="exam-dial-pct">
            <span className="exam-dial-pct-num">{pct}</span>
            <span className="exam-dial-pct-mark">%</span>
          </div>
        </div>

        {/* Stage heading */}
        <div className="exam-stage-wrap">
          <div className="exam-status">
            <span className="exam-status-dot"/>
            Examination in progress
          </div>
          <div className="exam-title">{currentStage?.label}…</div>
          <div className="exam-stage-principle">
            {currentStage?.principle || `Stage ${stageNum} of ${STAGES.length}`}
          </div>
        </div>

        {/* Stage log */}
        <div className="exam-log">
          {STAGES.map((s, i) => {
            const state = i < progress ? "done" : i === progress ? "active" : "pending";
            return (
              <div key={i} className={`exam-log-row ${state}`}>
                <span className="exam-log-check">
                  {state === "done" ? "✓" : state === "active" ? "›" : "·"}
                </span>
                <span className="exam-log-label">{s.label}</span>
                {s.principle && state !== "pending" && (
                  <span className="exam-log-principle">{s.principle}</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom notice */}
        <div className="exam-notice">
          <span className="mono-sm" style={{color:"var(--ink-faint)"}}>
            ORDO·SEMEION · CHARTER §§I–VI · NOTHING RETAINED UNLESS YOU SAVE IT
          </span>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   FINDINGS
   ============================================================ */
function Findings({ data, go, isExaminer, onUpdateNotes, onShare, onRevokeShare }) {
    if (!data) return null;
  const d = data.report;
  const [notes, setNotes] = React.useState(data.notes || "");
  const [notesSaving, setNotesSaving] = React.useState(false);
  const [notesSaved, setNotesSaved] = React.useState(false);
  const [shareUrl, setShareUrl] = React.useState(data.shareToken ? `${window.location.origin}/?share=${data.shareToken}` : null);
  const [shareLoading, setShareLoading] = React.useState(false);
  const [shareCopied, setShareCopied] = React.useState(false);
  const notesTimer = React.useRef(null);

  async function handleNotesChange(val) {
    setNotes(val);
    setNotesSaved(false);
    clearTimeout(notesTimer.current);
    notesTimer.current = setTimeout(async () => {
      if (!onUpdateNotes) return;
      setNotesSaving(true);
      await onUpdateNotes(data.id, val);
      setNotesSaving(false);
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2000);
    }, 800);
  }

  async function handleShare() {
    if (!onShare) return;
    setShareLoading(true);
    const result = await onShare(data.id);
    if (result.success) {
      setShareUrl(result.url);
      data.shareToken = result.token;
    }
    setShareLoading(false);
  }

  async function handleRevokeShare() {
    if (!onRevokeShare) return;
    await onRevokeShare(data.id);
    setShareUrl(null);
    data.shareToken = null;
  }

  function copyShareUrl() {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    });
  }
  const highlight = d.key_phrase || "";

  // ── Derived analytics (zero extra model tokens) ──
  const states = Array.isArray(d.states) ? d.states : [];
  const dominant = states.length ? states.reduce((a, b) => (b.intensity > a.intensity ? b : a)) : null;
  const pressureAvg = states.length
    ? Math.round(states.reduce((s, x) => s + (parseInt(x.intensity, 10) || 0), 0) / states.length)
    : 0;
  // Spread = how concentrated vs diffuse the affective attack is.
  const pressureSpread = states.length > 1
    ? Math.max(...states.map(s => s.intensity)) - Math.min(...states.map(s => s.intensity))
    : 0;
  const attackShape = pressureSpread >= 40 ? "Focused" : pressureSpread >= 20 ? "Layered" : "Diffuse";
  const wordCount = data.input.text.trim().split(/\s+/).filter(Boolean).length;
  const symbolCount = Array.isArray(d.symbols) ? d.symbols.length : 0;
  // Symbol density = operative symbols per 100 words → how saturated the text is.
  const symbolDensity = wordCount ? (symbolCount / wordCount * 100).toFixed(1) : "0";
  const dqv = parseFloat(d.drift_quotient) || 0;
  const driftLabel = dqv >= 0.7 ? "Severe divergence" : dqv >= 0.45 ? "Operative divergence"
    : dqv >= 0.2 ? "Minor divergence" : "Near coherence";

  return (
    <div className="findings">
      <header className="findings-head">
        <div>
          <div className="doc-no" style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12}}>
            <span>Document №{data.id} · {data.ts} · Charter §§I–VI</span>
            <button className="cta-ghost" style={{padding:"6px 14px",fontSize:"10px"}} onClick={() => go("verdict", data)}>
              Jump to Verdict →
            </button>
          </div>
          <h1>{d.title}</h1>
          <div className="artifact">
            <span style={{ color: "var(--ink-faint)" }}>Specimen ·</span> {d.artifact_type}
            <span style={{ margin: "0 12px", color: "var(--rule)" }}>|</span>
            <span style={{ color: "var(--ink-faint)" }}>Stance ·</span> {data.input.stance}
          </div>
        </div>
        <div className="head-meta">
          <div className="row"><span>Overall</span><span className="grade-big">{d.grade}</span></div>
          <div className="row">
            <span>Drift §VI</span>
            <span style={{ color: parseFloat(d.drift_quotient) >= 0.7 ? "var(--warn)" : parseFloat(d.drift_quotient) >= 0.4 ? "var(--accent)" : "var(--signal)", fontFamily:"var(--mono)", fontSize:18, fontStyle:"normal" }}>
              {typeof d.drift_quotient === "number" ? d.drift_quotient.toFixed(2) : d.drift_quotient || "—"}
            </span>
          </div>
          <div className="row"><span>Signal</span><span style={{ color: "var(--ink)" }}>{d.signal_integrity || d.asymmetry}</span></div>
          <div className="row"><span>Intensity</span><span style={{ color: "var(--ink)" }}>{d.intensity}/10</span></div>
          <div className="row"><span>Words</span><span style={{ color: "var(--ink)" }}>{data.input.text.trim().split(/\s+/).filter(Boolean).length}</span></div>
        </div>
      </header>

      {/* Re-examination delta — how this reading moved from the prior version */}
      {d._lineage && d._lineage.prev_drift != null && (() => {
        const prev = parseFloat(d._lineage.prev_drift);
        const delta = dqv - prev;
        const improved = delta < -0.001;
        const worsened = delta > 0.001;
        const dColor = improved ? "var(--signal)" : worsened ? "var(--warn)" : "var(--ink-dim)";
        return (
          <div className="reexam-delta" style={{ borderColor: dColor }}>
            <span className="reexam-delta-mark" style={{ color: dColor }}>⟲</span>
            <div className="reexam-delta-body">
              <div className="reexam-delta-title">Revision {d._lineage.version} · drift {improved ? "fell" : worsened ? "rose" : "held"}</div>
              <div className="reexam-delta-nums">
                <span>{prev.toFixed(2)}</span>
                <span className="reexam-delta-arrow">→</span>
                <span style={{ color: dColor }}>{dqv.toFixed(2)}</span>
                <span className="reexam-delta-chip" style={{ color: dColor, borderColor: dColor }}>
                  {delta >= 0 ? "+" : ""}{delta.toFixed(2)}
                </span>
                {d._lineage.prev_grade && (
                  <span className="reexam-delta-grade">{d._lineage.prev_grade} → {d.grade}</span>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Instrument Readout — derived analytics, no extra model cost */}
      <div className="readout">
        <div className="readout-cell">
          <div className="readout-val" style={{ color: dqv >= 0.7 ? "var(--warn)" : dqv >= 0.45 ? "var(--accent)" : "var(--signal)" }}>{dqv.toFixed(2)}</div>
          <div className="readout-label">Drift §VI</div>
          <div className="readout-sub">{driftLabel}</div>
        </div>
        <div className="readout-cell">
          <div className="readout-val">{pressureAvg}<small style={{fontSize:13,color:"var(--ink-faint)"}}>%</small></div>
          <div className="readout-label">Mean Affective Pressure</div>
          <div className="readout-sub">{attackShape} attack</div>
        </div>
        <div className="readout-cell">
          <div className="readout-val">{symbolDensity}</div>
          <div className="readout-label">Symbol Density</div>
          <div className="readout-sub">{symbolCount} symbols · {wordCount} words</div>
        </div>
        {dominant && (
          <div className="readout-cell">
            <div className="readout-val" style={{ fontSize: 19 }}>{dominant.name}</div>
            <div className="readout-label">Dominant Register</div>
            <div className="readout-sub">{dominant.intensity}% intensity</div>
          </div>
        )}
      </div>

      <div className="findings-grid">

        {/* Specimen — the corpus itself */}
        <section className="finding-section full">
          <div className="section-rule">
            <span className="no">§ 0.</span>
            <SectionName>Specimen</SectionName>
            <span className="gloss">— as received, unaltered</span>
            <span className="antiphon">{ANTIPHONS.specimen}</span>
          </div>
          <div className="specimen-card">
            <CornerPack />
            <div className="header">
              <span className="mono-sm" style={{ color: "var(--ink-faint)" }}>
                {data.input.medium} · {data.input.tags?.join(" · ") || "no tags"}
              </span>
              <span className="mono-sm" style={{ color: "var(--ink-faint)" }}>
                {data.input.text.trim().split(/\s+/).filter(Boolean).length} words
              </span>
            </div>
            <div className="specimen-text">
              <div className="lineno">
                {data.input.text.split(/\n/).map((_, i) => (
                  <div key={i}>{String(i + 1).padStart(2, "0")}</div>
                ))}
              </div>
              <div>
                {renderSpecimenLines(data.input.text, d.key_phrase)}
              </div>
            </div>
          </div>
        </section>

        {/* Synopsis */}
        <section className="finding-section full">
          <div className="section-rule">
            <span className="no">§ I.</span>
            <SectionName>Synopsis</SectionName>
            <span className="gloss">— what the specimen is doing</span>
            <span className="antiphon">{ANTIPHONS.synopsis}</span>
          </div>
          <p style={{ fontSize: 22, lineHeight: 1.45, color: "var(--ink)", maxWidth: "70ch", fontStyle: "italic" }}>
            “{d.synopsis}”
          </p>
        </section>

        {/* Most Operative Phrase */}
        <section className="finding-section full">
          <div className="section-rule">
            <span className="no">§ I·½.</span>
            <SectionName>Most Operative Phrase</SectionName>
            <span className="gloss">— the line that does the work</span>
            <span className="antiphon">{ANTIPHONS.operative}</span>
          </div>
          <div className="pull-quote">
            <CornerPack />
            <div className="lbl">Pulled from the specimen</div>
            <blockquote>{d.key_phrase || <span style={{color:"var(--ink-faint)",fontStyle:"normal",fontSize:16}}>No operative phrase isolated — specimen may be too short or too diffuse.</span>}</blockquote>
            {d.key_phrase && <div className="attribution">— Verbatim, line as it appears —</div>}
          </div>
        </section>

        {/* Symbol Inventory */}
        <section className="finding-section full">
          <div className="section-rule">
            <span className="no">§ II.</span>
            <SectionName>Symbol Inventory</SectionName>
            <span className="gloss">— the operative signs and their cargo</span>
            <span className="antiphon">{ANTIPHONS.symbols}</span>
          </div>
          <div className="symbols">
            {d.symbols.map((s, i) => (
              <div key={i} className="symbol">
                <div className="glyph">
                  <Glyph index={s.glyph_index ?? i} size={56} />
                </div>
                <div className="body">
                  <h3>{s.name}</h3>
                  <p>{s.description}</p>
                  {s.mechanism && (
                    <p style={{ fontSize: 13.5, color: "var(--ink-faint)", marginTop: 6, fontStyle: "italic" }}>
                      Mechanism · {s.mechanism}
                    </p>
                  )}
                  <span className="tag">Activates · {s.activates}</span>
                </div>
                <div className="effect">{s.effect}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Register — borrowed voice */}
        {d.register && (
          <section className="finding-section full">
            <div className="section-rule">
              <span className="no">§ II·½.</span>
              <SectionName>Register</SectionName>
              <span className="gloss">— the borrowed voice</span>
            </div>
            <div style={{ display: "flex", gap: 24, alignItems: "baseline", flexWrap: "wrap" }}>
              <h2 style={{ fontSize: 24, fontStyle: "italic" }}>{d.register.name}</h2>
              {d.register.borrowed_from && (
                <span className="tag">Borrowed from · {d.register.borrowed_from}</span>
              )}
            </div>
            <p style={{ maxWidth: "70ch", marginTop: 8 }}>{d.register.description}</p>
          </section>
        )}

        {/* Narrative Frame */}
        <section className="finding-section">
          <div className="section-rule">
            <span className="no">§ III.</span>
            <SectionName>Narrative Frame</SectionName>
            <span className="antiphon">{ANTIPHONS.narrative}</span>
          </div>
          <h2>{d.narrative.title}</h2>
          <p>{d.narrative.frame}</p>
          <div style={{ borderLeft: "2px solid var(--accent)", paddingLeft: 18, marginTop: 8 }}>
            <span className="label" style={{ display: "block", marginBottom: 6 }}>You are invited to become…</span>
            <p style={{ fontStyle: "italic", color: "var(--ink)", fontSize: 19, margin: 0 }}>{d.narrative.invitation}</p>
          </div>
          {d.narrative.tension && (
            <div style={{ marginTop: 16 }}>
              <span className="label" style={{ display: "block", marginBottom: 6 }}>Unresolved tension</span>
              <p style={{ fontSize: 15, color: "var(--ink-dim)", margin: 0 }}>{d.narrative.tension}</p>
            </div>
          )}
        </section>

        {/* State Induction */}
        <section className="finding-section">
          <div className="section-rule">
            <span className="no">§ IV.</span>
            <SectionName>Affective Induction</SectionName>
            <span className="antiphon">{ANTIPHONS.states}</span>
          </div>
          <p>The specimen primes the following affective registers, in descending order of pressure:</p>
          <div className="states">
            {d.states.map((s, i) => (
              <div key={i} className="state-row">
                <span className="name">{s.name}</span>
                <span className="bar"><span style={{ width: `${s.intensity}%` }}></span></span>
                <span className="pct">{s.intensity}%</span>
              </div>
            ))}
          </div>
          {states.length > 0 && (
            <div className="states-readout">
              <span><strong style={{color:"var(--ink)"}}>{attackShape}</strong> attack · spread {pressureSpread} pts</span>
              <span>Mean pressure <strong style={{color:"var(--ink)"}}>{pressureAvg}%</strong></span>
              {dominant && <span>Carried by <strong style={{color:"var(--accent)"}}>{dominant.name}</strong></span>}
            </div>
          )}
        </section>

        {/* Ritual Mechanics */}
        <section className="finding-section full">
          <div className="section-rule">
            <span className="no">§ V.</span>
            <SectionName>Ritual Mechanics</SectionName>
            <span className="gloss">— the binding apparatus</span>
            <span className="antiphon">{ANTIPHONS.mechanics}</span>
          </div>
          <div className="mechanics">
            {d.mechanics.map((m, i) => (
              <div key={i} className="mech">
                <div className="label">{m.kind}</div>
                <div className="title">{m.title}</div>
                <div className="desc">{m.description}</div>
                {m.counter && (
                  <div className="mech-counter">
                    <span className="mech-counter-label">Counter</span>
                    {m.counter}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Rhetorical Devices */}
        {Array.isArray(d.rhetorical_devices) && d.rhetorical_devices.length > 0 && (
          <section className="finding-section full">
            <div className="section-rule">
              <span className="no">§ V·½.</span>
              <SectionName>Rhetorical Devices</SectionName>
              <span className="gloss">— the named techniques in operation</span>
            </div>
            <div className="devices">
              {d.rhetorical_devices.map((r, i) => (
                <div key={i} className="device">
                  <div className="device-name">{r.device}</div>
                  <blockquote className="device-instance">“{r.instance}”</blockquote>
                  <div className="device-function">{r.function}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Strategic Omissions */}
        {Array.isArray(d.omissions) && d.omissions.length > 0 && (
          <section className="finding-section full">
            <div className="section-rule">
              <span className="no">§ V·¾.</span>
              <SectionName>Strategic Omissions</SectionName>
              <span className="gloss">— what is conspicuously absent</span>
            </div>
            <div className="omissions">
              {d.omissions.map((o, i) => (
                <div key={i} className="omission">
                  <span className="omission-mark">∅</span>
                  <div>
                    <div className="omission-item">{o.item}</div>
                    <div className="omission-sig">{o.significance}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Power Dynamics */}
        <section className="finding-section full">
          <div className="section-rule">
            <span className="no">§ VI.</span>
            <SectionName>Power Dynamics</SectionName>
            <span className="antiphon">{ANTIPHONS.power}</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 32, marginTop: 8 }}>
            <div>
              <span className="label">Deployer</span>
              <h2 style={{ marginTop: 8, fontSize: 22 }}>{d.power.deployer}</h2>
              <p style={{ fontSize: 14 }}>{d.power.deployer_note}</p>
            </div>
            <div>
              <span className="label">Target</span>
              <h2 style={{ marginTop: 8, fontSize: 22 }}>{d.power.target}</h2>
              <p style={{ fontSize: 14 }}>{d.power.target_note}</p>
            </div>
            <div>
              <span className="label">Asymmetry</span>
              <h2 style={{ marginTop: 8, fontSize: 22, color: "var(--warn)" }}>{d.asymmetry}</h2>
              <p style={{ fontSize: 14 }}>{d.power.asymmetry_note}</p>
            </div>
          </div>
          {d.power.leverage && (
            <div style={{ marginTop: 24, borderTop: "1px solid var(--rule-soft)", paddingTop: 16 }}>
              <span className="label" style={{ display: "block", marginBottom: 6 }}>Source of leverage</span>
              <p style={{ fontSize: 15, color: "var(--ink-dim)", margin: 0, maxWidth: "70ch" }}>{d.power.leverage}</p>
            </div>
          )}
        </section>

      </div>

        {/* ── EXAMINER NOTES ── */}
        {isExaminer && (
          <div className="findings-notes-section">
            <div className="findings-notes-header">
              <span className="mono-sm" style={{color:"var(--ink-dim)"}}>Examiner Notes</span>
              <span className="mono-sm" style={{color: notesSaved?"var(--signal)":notesSaving?"var(--accent)":"var(--ink-faint)"}}>
                {notesSaved ? "✓ Saved" : notesSaving ? "Saving…" : "Auto-saved"}
              </span>
            </div>
            <textarea
              className="findings-notes-textarea"
              value={notes}
              onChange={e => handleNotesChange(e.target.value)}
              placeholder="Add your interpretation, context, or follow-up questions…"
            />
          </div>
        )}
        {/* ── SHARE LINK ── */}
        {isExaminer && (
          <div className="findings-share-section">
            <div className="findings-notes-header">
              <span className="mono-sm" style={{color:"var(--ink-dim)"}}>Share This Examination</span>
              <span className="mono-sm" style={{color:"var(--ink-faint)"}}>Valid 30 days · Public view</span>
            </div>
            {shareUrl ? (
              <div className="findings-share-url-row">
                <div className="findings-share-url">{shareUrl}</div>
                <button className={`findings-share-copy ${shareCopied?"copied":""}`} onClick={copyShareUrl}>
                  {shareCopied ? "✓ Copied" : "Copy"}
                </button>
                <button className="findings-share-revoke" onClick={handleRevokeShare}>Revoke</button>
              </div>
            ) : (
              <button className="cta-ghost" style={{fontSize:"10px",padding:"10px 16px"}}
                onClick={handleShare} disabled={shareLoading}>
                {shareLoading ? "Generating link…" : "□ Generate Share Link →"}
              </button>
            )}
          </div>
        )}


      <div className="action-row" style={{ marginTop: 56, paddingTop: 28, borderTop: "1px solid var(--rule)" }}>
        <button className="cta-primary" onClick={() => go("verdict", data)}>
          Proceed to Ethics Verdict <span className="arr">→</span>
        </button>
        <button className="cta-ghost" onClick={() => go("intake", { preload: reexamPreload(data) })}>⟲ Re-examine</button>
        <button className="cta-ghost" onClick={() => go("intake")}>Examine Another</button>
        <button className="cta-ghost" onClick={() => go("archive")}>Archive</button>
      </div>
    </div>
  );
}

/* ============================================================
   ETHICS VERDICT
   ============================================================ */
const PRINCIPLES = [
  { k: "transparency", n: "I.", name: "Transparency", creed: "Disclose when you're attempting to influence" },
  { k: "consent",       n: "II.", name: "Consent", creed: "Obtain agreement before deploying" },
  { k: "harm_reduction",n: "III.", name: "Harm Reduction", creed: "Consider and minimize unintended consequences" },
  { k: "power_awareness",n: "IV.", name: "Power Awareness", creed: "Acknowledge asymmetries; use influence responsibly" },
  { k: "empirical",     n: "V.", name: "Empirical Grounding", creed: "Test claims; don't assert what you can't demonstrate" },
  { k: "cultural",      n: "VI.", name: "Cultural Respect", creed: "Honor symbolic systems; don't appropriate or trivialize" },
];

function Verdict({ data, go, isExaminer, onExportPDF }) {
  if (!data) return null;
  const d = data.report;
  const cls = d.verdict_class; // 'pass' | 'mixed' | 'fail'
  const stampColor = cls === "pass" ? "var(--signal)" : cls === "mixed" ? "var(--accent)" : "var(--warn)";
  const [showShare, setShowShare] = React.useState(false);

  // ── Receiver Briefing — derived entirely from existing report data
  //    (zero extra model tokens / latency). A plain-language risk readout. ──
  const dq = parseFloat(d.drift_quotient) || 0;
  const intensity = parseInt(d.intensity, 10) || 0;
  const scores = PRINCIPLES.map(p => parseInt(d.scorecard?.[p.k]?.score, 10)).filter(n => !isNaN(n));
  const meanScore = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : null;
  const weakest = PRINCIPLES
    .map(p => ({ name: p.name, score: parseInt(d.scorecard?.[p.k]?.score, 10) }))
    .filter(x => !isNaN(x.score))
    .sort((a, b) => a.score - b.score)[0];
  const exposure = dq >= 0.7 || intensity >= 8 ? "HIGH"
    : dq >= 0.45 || intensity >= 6 ? "ELEVATED" : "LOW";
  const exposureColor = exposure === "HIGH" ? "var(--warn)" : exposure === "ELEVATED" ? "var(--accent)" : "var(--signal)";
  const briefing = exposure === "HIGH"
    ? "This specimen works hard on its receiver and diverges sharply from its stated purpose. Engage it deliberately, not passively."
    : exposure === "ELEVATED"
    ? "This specimen applies real persuasive pressure. Worth reading twice before you accept its frame."
    : "This specimen is comparatively restrained. Its mechanism stays close to its stated purpose.";
  return (
    <div className="verdict-page">
      {showShare && <ShareCard data={data} onClose={() => setShowShare(false)} />}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:18}}>
        <div className="label">§ 04 · Examination · Ethics Verdict</div>
        <div className="compliance-badge" style={{
          background: cls==="pass" ? "rgba(107,138,122,0.12)" : cls==="fail" ? "rgba(176,74,58,0.12)" : "rgba(201,165,112,0.12)",
          border: `1px solid ${stampColor}`,
          color: stampColor,
          padding: "4px 12px",
          fontFamily: "var(--mono)",
          fontSize: "9px",
          letterSpacing: "0.14em",
          textTransform: "uppercase"
        }}>
          {cls === "pass" ? "CHARTER COMPLIANT" : cls === "fail" ? "CHARTER VIOLATION" : "CHARTER ADVISORY"}
        </div>
      </div>

      <div className="verdict-banner" style={{ borderColor: stampColor }}>
        <div className="sigil" style={{ color: stampColor }}>
          <AdaptiveSigil size={96} />
        </div>
        <div style={{flex:1}}>
          <h2>{d.verdict_headline}</h2>
          <p>{d.verdict_blurb}</p>
          {/* Drift Quotient prominent display */}
          <div className="verdict-dq-row">
            <span className="verdict-dq-label">DRIFT QUOTIENT §VI</span>
            <span className="verdict-dq-value" style={{
              color: parseFloat(d.drift_quotient) >= 0.7 ? "var(--warn)"
                : parseFloat(d.drift_quotient) >= 0.4 ? "var(--accent)"
                : "var(--signal)"
            }}>
              {typeof d.drift_quotient === "number" ? d.drift_quotient.toFixed(2) : "—"}
            </span>
            <div className="verdict-dq-bar">
              <div className="verdict-dq-fill" style={{
                width: `${Math.round((d.drift_quotient || 0) * 100)}%`,
                background: parseFloat(d.drift_quotient) >= 0.7 ? "var(--warn)"
                  : parseFloat(d.drift_quotient) >= 0.4 ? "var(--accent)"
                  : "var(--signal)"
              }}/>
            </div>
          </div>
        </div>
        <div className="stamp" style={{ color: stampColor, borderColor: stampColor }}>
          Charter Stamp
          <span className="big" style={{ color: stampColor }}>{d.grade}</span>
        </div>
      </div>

      {/* Receiver Briefing — derived risk readout */}
      <div className="receiver-briefing">
        <div className="receiver-briefing-head">
          <span className="label">Receiver Briefing</span>
          <span className="receiver-exposure" style={{ color: exposureColor, borderColor: exposureColor }}>
            {exposure} EXPOSURE
          </span>
        </div>
        <p className="receiver-briefing-text">{briefing}</p>
        <div className="receiver-metrics">
          <div className="receiver-metric">
            <span className="receiver-metric-val" style={{ color: dq >= 0.7 ? "var(--warn)" : dq >= 0.4 ? "var(--accent)" : "var(--signal)" }}>{dq.toFixed(2)}</span>
            <span className="receiver-metric-label">Drift §VI</span>
          </div>
          <div className="receiver-metric">
            <span className="receiver-metric-val">{intensity}/10</span>
            <span className="receiver-metric-label">Intensity</span>
          </div>
          {meanScore !== null && (
            <div className="receiver-metric">
              <span className="receiver-metric-val" style={{ color: meanScore >= 7 ? "var(--signal)" : meanScore >= 4 ? "var(--accent)" : "var(--warn)" }}>{meanScore.toFixed(1)}</span>
              <span className="receiver-metric-label">Mean Charter</span>
            </div>
          )}
          {weakest && (
            <div className="receiver-metric">
              <span className="receiver-metric-val" style={{ color: "var(--warn)", fontSize: 16 }}>{weakest.name}</span>
              <span className="receiver-metric-label">Weakest Principle · {weakest.score}/10</span>
            </div>
          )}
        </div>
      </div>

      <div className="principles">
        {PRINCIPLES.map((p, i) => {
          const s = d.scorecard[p.k] || { score: 5, finding: "No finding recorded." };
          const score = s.score;
          const scoreClass = score >= 7 ? "s-pass" : score >= 4 ? "s-mixed" : "s-fail";
          const scoreColor = score >= 7 ? "var(--signal)" : score >= 4 ? "var(--accent)" : "var(--warn)";
          const sevColor = s.severity === "violation" ? "var(--warn)"
            : s.severity === "concern" ? "var(--accent)"
            : s.severity === "exemplary" ? "var(--signal)" : "var(--ink-faint)";
          return (
            <div className="principle" key={p.k}>
              <span className="ord">{p.n}</span>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                  <h3 style={{margin:0}}>{p.name}</h3>
                  {s.severity && (
                    <span className="severity-badge" style={{ color: sevColor, borderColor: sevColor }}>
                      {s.severity}
                    </span>
                  )}
                </div>
                <div className="creed">{p.creed}</div>
                <p>{s.finding}</p>
                {s.evidence && (
                  <div className="principle-evidence">
                    <span className="principle-evidence-label">Evidence</span>
                    {s.evidence}
                  </div>
                )}
                {/* Score bar */}
                <div className="scorecard-bar-wrap">
                  <div className="scorecard-bar-track">
                    <div className="scorecard-bar-fill"
                      style={{ width: `${score * 10}%`, background: scoreColor }}/>
                  </div>
                </div>
              </div>
              <div className={`score ${scoreClass}`}>
                {score}<small>/10</small>
              </div>
            </div>
          );
        })}
      </div>

      {/* Key Tension */}
      {d.key_tension && (
        <div className="verdict-tension">
          <div className="label" style={{ marginBottom: 10 }}>Central Tension</div>
          <h2 style={{ fontStyle: "italic", fontSize: 26, marginBottom: 12 }}>{d.key_tension.title}</h2>
          <p style={{ color: "var(--ink-dim)", fontSize: 16, lineHeight: 1.65, maxWidth: "75ch" }}>{d.key_tension.body}</p>
        </div>
      )}

      <div style={{ marginTop: 56, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40 }}>
        <div>
          <div className="label" style={{ marginBottom: 10 }}>Counter-praxis</div>
          <h2 style={{ fontStyle: "italic", fontSize: 26, marginBottom: 12 }}>If you were the target</h2>
          <p style={{ color: "var(--ink-dim)", fontSize: 16, lineHeight: 1.6 }}>{d.counter_praxis}</p>
        </div>
        <div>
          <div className="label" style={{ marginBottom: 10 }}>Ethical Re-Deployment</div>
          <h2 style={{ fontStyle: "italic", fontSize: 26, marginBottom: 12 }}>If you were the deployer</h2>
          <p style={{ color: "var(--ink-dim)", fontSize: 16, lineHeight: 1.6 }}>{d.ethical_redeployment}</p>
          {d.redeployment_example && (
            <div className="redeploy-example">
              <span className="redeploy-example-label">Worked example</span>
              <p>{d.redeployment_example}</p>
            </div>
          )}
        </div>
      </div>

      {/* Precedent + Confidence */}
      {(d.precedent || d.confidence) && (
        <div className="verdict-footer-meta">
          {d.precedent && (
            <div className="verdict-precedent">
              <span className="label" style={{ display: "block", marginBottom: 8 }}>Precedent · Comparable Work</span>
              <p style={{ color: "var(--ink-dim)", fontSize: 15, lineHeight: 1.6, margin: 0 }}>{d.precedent}</p>
            </div>
          )}
          {d.confidence && (
            <div className="verdict-confidence">
              <span className="label" style={{ display: "block", marginBottom: 8 }}>Instrument Confidence</span>
              <div className="verdict-confidence-val" style={{
                color: d.confidence === "HIGH" ? "var(--signal)" : d.confidence === "LOW" ? "var(--warn)" : "var(--accent)"
              }}>{d.confidence}</div>
              {d.confidence_note && <p style={{ color: "var(--ink-faint)", fontSize: 13.5, lineHeight: 1.5, margin: "6px 0 0" }}>{d.confidence_note}</p>}
            </div>
          )}
        </div>
      )}

      <div className="action-row" style={{ marginTop: 56, paddingTop: 28, borderTop: "1px solid var(--rule)" }}>
        <button className="cta-primary" onClick={() => go("intake", { preload: reexamPreload(data) })}>⟲ Re-examine <span className="arr">→</span></button>
        <button className="cta-ghost" onClick={() => go("intake")}>Examine Another</button>
        <button className="cta-ghost" onClick={() => setShowShare(true)}>□ Share Findings</button>
        {isExaminer
          ? <button className="cta-ghost" onClick={onExportPDF}>⬇ Export PDF</button>
          : <button className="cta-ghost" style={{opacity:0.5}} title="Requires Examiner designation">⬇ PDF (Examiner)</button>
        }
        <button className="cta-ghost" onClick={() => go("findings", data)}>← Back to Findings</button>
        <button className="cta-ghost" onClick={() => go("archive")}>Archive</button>
      </div>
    </div>
  );
}

/* ============================================================
   ARCHIVE
   ============================================================ */
function Archive({ archive, go, isExaminer, onDelete, setArchive, onCompare, onSetCollection }) {
  const [filter, setFilter] = React.useState("all");
  const [sort, setSort] = React.useState("newest");
  const [query, setQuery] = React.useState("");
  const [collectionFilter, setCollectionFilter] = React.useState("all");
  const [compareMode, setCompareMode] = React.useState(false);

  // Distinct collection names present in the archive.
  const collections = React.useMemo(() => {
    const set = new Set();
    archive.forEach(a => { const c = a.report?._collection; if (c) set.add(c); });
    return [...set].sort();
  }, [archive]);

  // Move an examination to a collection (or null to remove). Optimistic UI.
  async function moveToCollection(entry, name) {
    if (setArchive) setArchive(prev => prev.map(x =>
      x.id === entry.id ? { ...x, report: { ...(x.report||{}), _collection: name || null } } : x));
    if (onSetCollection) await onSetCollection(entry, name || null);
  }
  function promptCollection(entry) {
    const current = entry.report?._collection || "";
    const name = window.prompt(
      "Move to collection (type a name, or leave blank to remove from collection):",
      current
    );
    if (name === null) return; // cancelled
    moveToCollection(entry, name.trim() || null);
  }
  const [selected, setSelected] = React.useState([]); // up to 2 ids

  function toggleSelect(id) {
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 2) return [prev[1], id]; // keep last 2
      return [...prev, id];
    });
  }
  function runCompare() {
    if (selected.length !== 2) return;
    const a = archive.find(x => x.id === selected[0]);
    const b = archive.find(x => x.id === selected[1]);
    if (a && b && onCompare) onCompare(a, b);
  }

  const q = query.trim().toLowerCase();
  const filtered = archive.filter(a => {
    // collection (folder) filter
    if (collectionFilter === "__none" && a.report?._collection) return false;
    if (collectionFilter !== "all" && collectionFilter !== "__none" && a.report?._collection !== collectionFilter) return false;
    // verdict-class filter
    if (filter === "pass" && a.verdictClass !== "clean") return false;
    if (filter === "mixed" && a.verdictClass !== "mixed") return false;
    if (filter === "fail" && a.verdictClass !== "corrosive") return false;
    // free-text search across title, headline, artifact, medium, tags, specimen
    if (q) {
      const hay = [
        a.title, a.verdictHeadline, a.report?.artifact_type,
        a.input?.medium, a.input?.stance, a.grade,
        (a.input?.tags || []).join(" "),
        a.input?.text,
      ].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sort === "drift") {
      const da = parseFloat(a.report?.drift_quotient || 0);
      const db = parseFloat(b.report?.drift_quotient || 0);
      return db - da;
    }
    if (sort === "grade") return (a.grade || "").localeCompare(b.grade || "");
    return 0; // newest = archive order
  });

  // Stats
  const meanDrift = archive.length
    ? (archive.reduce((sum, a) => sum + parseFloat(a.report?.drift_quotient || 0), 0) / archive.length).toFixed(2)
    : "—";
  const grades = archive.map(a => a.grade).filter(Boolean);
  const meanGrade = grades.length ? grades[Math.floor(grades.length / 2)] : "—";

  return (
    <div className="archive">
      {/* Header */}
      <header className="archive-head">
        <div>
          <div className="label" style={{marginBottom:8}}>§ 05 · {isExaminer ? "Account Archive" : "Session Archive"} · Ordo Semeion</div>
          <h1>Archive of Examinations</h1>
          <div className="count">
            {String(archive.length).padStart(3, "0")} entries · {isExaminer
              ? "saved to your account · delete to erase permanently"
              : "session-local · sign in to save"}
          </div>
        </div>
        {/* Stats panel */}
        {archive.length > 0 && (
          <div className="archive-stats">
            <div className="archive-stat">
              <span className="archive-stat-val">{String(archive.length).padStart(3,"0")}</span>
              <span className="archive-stat-label">Specimens</span>
            </div>
            <div className="archive-stat">
              <span className="archive-stat-val" style={{color: parseFloat(meanDrift) >= 0.7 ? "var(--warn)" : parseFloat(meanDrift) >= 0.4 ? "var(--accent)" : "var(--signal)"}}>{meanDrift}</span>
              <span className="archive-stat-label">Mean Drift §VI</span>
            </div>
            <div className="archive-stat">
              <span className="archive-stat-val" style={{color:"var(--accent)"}}>{meanGrade}</span>
              <span className="archive-stat-label">Median Grade</span>
            </div>
            <div className="archive-stat">
              <span className="archive-stat-val">{archive.filter(a=>a.verdictClass==="clean").length}</span>
              <span className="archive-stat-label">Pass</span>
            </div>
            <div className="archive-stat">
              <span className="archive-stat-val" style={{color:"var(--warn)"}}>{archive.filter(a=>a.verdictClass==="corrosive").length}</span>
              <span className="archive-stat-label">Fail</span>
            </div>
          </div>
        )}
      </header>

      {archive.length === 0 && (
        <div className="archive-empty">
          <Glyph index={4} size={64} color="var(--ink-faint)" />
          <p className="empty-hint">The archive is empty. Examinations conducted this session appear here.</p>
          <button className="cta-primary" onClick={() => go("intake")} style={{marginTop:12}}>
            Begin First Examination <span className="arr">→</span>
          </button>
        </div>
      )}

      {archive.length > 0 && (
        <>
          {/* Search */}
          <div className="archive-search">
            <span className="archive-search-icon">⌕</span>
            <input
              className="archive-search-input"
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search title, verdict, specimen text, medium, tag…"
            />
            {query && (
              <button className="archive-search-clear" onClick={() => setQuery("")}>✕</button>
            )}
          </div>

          {/* Collections (folders) */}
          {collections.length > 0 && (
            <div className="archive-collections">
              <span className="mono-sm" style={{color:"var(--ink-faint)"}}>Collections:</span>
              <button className={`chip ${collectionFilter==="all"?"on":""}`} onClick={() => setCollectionFilter("all")}>All</button>
              {collections.map(c => (
                <button key={c} className={`chip ${collectionFilter===c?"on":""}`} onClick={() => setCollectionFilter(c)}>
                  ⊟ {c} ({archive.filter(a => a.report?._collection === c).length})
                </button>
              ))}
              <button className={`chip ${collectionFilter==="__none"?"on":""}`} onClick={() => setCollectionFilter("__none")}>Unfiled</button>
            </div>
          )}

          {/* Controls */}
          <div className="archive-controls">
            <div className="archive-filters">
              {["all","pass","mixed","fail"].map(f => (
                <button key={f} className={`chip ${filter===f?"on":""}`} onClick={() => setFilter(f)}>
                  {f === "all" ? `All (${archive.length})` : f === "pass" ? `Pass (${archive.filter(a=>a.verdictClass==="clean").length})` : f === "mixed" ? `Mixed (${archive.filter(a=>a.verdictClass==="mixed").length})` : `Fail (${archive.filter(a=>a.verdictClass==="corrosive").length})`}
                </button>
              ))}
            </div>
            <div className="archive-sort">
              <span className="mono-sm" style={{color:"var(--ink-faint)"}}>Sort:</span>
              {["newest","drift","grade"].map(s => (
                <button key={s} className={`chip ${sort===s?"on":""}`} onClick={() => setSort(s)}>
                  {s === "newest" ? "Newest" : s === "drift" ? "Drift §VI" : "Grade"}
                </button>
              ))}
              {archive.length >= 2 && (
                <button className={`archive-compare-btn ${compareMode?"on":""}`}
                  onClick={() => { setCompareMode(m => !m); setSelected([]); }}>
                  ⊞ {compareMode ? "Exit Compare" : "Compare (The Ledger)"}
                </button>
              )}
            </div>
          </div>

          {compareMode && (
            <div className="cmp-bar">
              <span className="cmp-bar-text">
                {selected.length === 0 ? "Select two examinations to cross-examine."
                  : selected.length === 1 ? "Select one more."
                  : "Two selected — ready."}
              </span>
              <div style={{display:"flex",gap:8}}>
                <button className="cta-primary" style={{padding:"8px 16px",fontSize:"11px",opacity:selected.length===2?1:0.5}}
                  disabled={selected.length!==2} onClick={runCompare}>
                  Cross-examine → (The Ledger)
                </button>
                <button className="cta-ghost" style={{padding:"8px 16px",fontSize:"11px"}}
                  onClick={() => { setCompareMode(false); setSelected([]); }}>Cancel</button>
              </div>
            </div>
          )}

          {sorted.length === 0 && (
            <div className="archive-empty" style={{padding:"48px 20px"}}>
              <p className="empty-hint">No examinations match{q ? ` "${query}"` : " this filter"}.</p>
              <button className="cta-ghost" onClick={() => { setQuery(""); setFilter("all"); }} style={{marginTop:8}}>
                Clear filters
              </button>
            </div>
          )}

          {/* Case file cards */}
          {sorted.length > 0 && (
          <div className="archive-cards">
            {sorted.map((a, idx) => {
              const dq = parseFloat(a.report?.drift_quotient || 0);
              const dqColor = dq >= 0.7 ? "var(--warn)" : dq >= 0.4 ? "var(--accent)" : "var(--signal)";
              const gradeColor = a.verdictClass === "clean" ? "var(--signal)" : a.verdictClass === "mixed" ? "var(--accent)" : "var(--warn)";
              const isSel = selected.includes(a.id);
              return (
                <div key={a.id}
                  className={`archive-card ${compareMode ? "archive-card--selectable" : ""} ${isSel ? "archive-card--selected" : ""}`}
                  onClick={() => compareMode ? toggleSelect(a.id) : go("findings", a)}>
                  {compareMode && (
                    <div className="archive-card-checkbox">{isSel ? (selected.indexOf(a.id)+1) : ""}</div>
                  )}
                  {/* Card header */}
                  <div className="archive-card-head">
                    <div className="archive-card-sigil">
                      <GeneratedSigil seed={a.id} size={40} color="var(--accent)"/>
                    </div>
                    <div className="archive-card-id">
                      <span className="mono-sm" style={{color:"var(--ink-faint)"}}>{a.id}</span>
                      <span className="mono-sm" style={{color:"var(--ink-faint)"}}>{a.ts}</span>
                    </div>
                    <div className="archive-card-grade" style={{color: gradeColor}}>{a.grade}</div>
                  </div>
                  {/* Title */}
                  <div className="archive-card-title">{a.title}</div>
                  {/* Meta row */}
                  <div className="archive-card-meta">
                    <span className="chip" style={{cursor:"default",padding:"3px 8px",fontSize:"9px"}}>{a.input.medium}</span>
                    <span className="chip" style={{cursor:"default",padding:"3px 8px",fontSize:"9px"}}>{a.input.stance}</span>
                    <div style={{flex:1}}/>
                    {/* Drift bar */}
                    <div className="archive-card-drift">
                      <span className="mono-sm" style={{color:"var(--ink-faint)"}}>Drift</span>
                      <div className="archive-dq-track">
                        <div className="archive-dq-fill" style={{width:`${dq*100}%`, background: dqColor}}/>
                      </div>
                      <span className="mono-sm" style={{color: dqColor}}>{dq.toFixed(2)}</span>
                    </div>
                  </div>
                  {/* Verdict headline */}
                  <div className="archive-card-verdict">
                    <span className={`verdict v-${a.verdictClass}`} style={{fontSize:"13px"}}>{a.verdictHeadline}</span>
                  </div>
                  {a.report?._collection && (
                    <div className="archive-card-collection">⊟ {a.report._collection}</div>
                  )}
                  {isExaminer && !compareMode && (
                    <button className="archive-card-move"
                      title="Move to collection"
                      onClick={e => { e.stopPropagation(); promptCollection(a); }}>⊟</button>
                  )}
                  {isExaminer && !compareMode && (
                    <button className="archive-card-delete"
                      title="Permanently delete — removes the specimen text and findings from your account"
                      onClick={e => {
                        e.stopPropagation();
                        const ok = window.confirm(
                          `Permanently delete "${a.title}"?\n\nThis erases the specimen text and its findings from your account. This cannot be undone.`
                        );
                        if (!ok) return;
                        if (onDelete) onDelete(a.id);
                        if (setArchive) setArchive(prev => prev.filter(x => x.id !== a.id));
                      }}>✕</button>
                  )}
                  <div className="archive-card-arrow">→</div>
                </div>
              );
            })}
          </div>
          )}
        </>
      )}
    </div>
  );
}



/* ============================================================
   UPGRADE SCREEN — Examiner Designation Gate
   ============================================================ */
function UpgradeScreen({ onClose, onCheckout, onPayPal, packs = [], signedIn, credits = 0, onRequireAuth }) {
  const [loading, setLoading] = React.useState(null);
  const [method, setMethod] = React.useState("card"); // "card" | "paypal"
  const [selected, setSelected] = React.useState(() => {
    const rec = packs.find(p => p.recommended);
    return rec ? rec.id : (packs[0]?.id || "pack_50");
  });

  async function handleBuy() {
    if (!signedIn) { onRequireAuth?.(); return; }
    if (PAYPAL_ENABLED && method === "paypal") {
      setLoading("paypal");
      await onPayPal(selected);
    } else {
      setLoading("card");
      await onCheckout(selected);
    }
    setLoading(null);
  }

  return (
    <div className="upgrade-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="upgrade-modal">

        {/* Sigil */}
        <div className="upgrade-sigil">
          <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
            <circle cx="40" cy="40" r="36" stroke="var(--accent)" strokeWidth="0.8" opacity="0.4"/>
            <circle cx="40" cy="40" r="24" stroke="var(--accent)" strokeWidth="0.6" opacity="0.3"/>
            {[0,60,120,180,240,300].map((deg,i)=>{const r=24,rad=deg*Math.PI/180;return <circle key={i} cx={40+Math.cos(rad)*r} cy={40+Math.sin(rad)*r} r="2.5" fill="var(--accent)" opacity="0.5"/>;})}
            <path d="M40 27 C43.5 32 46 36 46 40 C46 45 43.3 48 40 48 C36.7 48 34 45 34 40 C34 36 36.5 32 40 27Z" fill="var(--ink)" opacity="0.85"/>
            <circle cx="40" cy="40" r="2" fill="var(--accent)" opacity="0.6"/>
          </svg>
        </div>

        {/* Header */}
        <div className="upgrade-header">
          <div className="label" style={{marginBottom:10}}>ORDO · SEMEION · MMXXVI</div>
          <h2 className="upgrade-title">Examination Credits</h2>
          <p className="upgrade-subtitle">
            {signedIn
              ? `You hold ${credits} credit${credits === 1 ? "" : "s"}. Acquire more — one credit, one examination.`
              : "One credit, one examination. Credits never expire."}
          </p>
        </div>

        {/* Lore */}
        <div className="upgrade-lore">
          <p>
            The Ordo Semeion issues examination credits to those who require continued access
            to the instrument. Each credit funds one full audit — findings and verdict — with
            persistent archive, PDF export, and shareable links.
          </p>
          <p style={{marginTop:12, color:"var(--ink-faint)"}}>
            Credits do not expire. No subscription. The Charter remains; nothing is
            retained unless you save it to your archive.
          </p>
        </div>

        {/* Packs */}
        <div className="upgrade-plans">
          {packs.map(p => (
            <div
              key={p.id}
              className={`upgrade-plan ${p.recommended ? "upgrade-plan--featured" : ""} ${selected === p.id ? "upgrade-plan--selected" : ""}`}
              onClick={() => setSelected(p.id)}
              style={{cursor:"pointer", outline: selected === p.id ? "1px solid var(--accent)" : "none"}}
            >
              {p.recommended && <div className="upgrade-plan-badge">RECOMMENDED</div>}
              {p.tier && <div className="upgrade-plan-tier">{p.tier}</div>}
              <div className="upgrade-plan-name">{p.credits} Examinations</div>
              <div className="upgrade-plan-price">
                <span className="upgrade-plan-amount">{p.price}</span>
              </div>
              <div className="upgrade-plan-saving">{p.saving || p.per}</div>
              {p.blurb && <div className="upgrade-plan-blurb">{p.blurb}</div>}
              <ul className="upgrade-plan-features">
                <li>{p.credits} full audits</li>
                <li>Persistent archive · PDF · share links</li>
                {p.tier === "Examiner" && <li>Examiner designation on shares</li>}
                {p.tier === "Adept" && <li>Adept designation + priority access</li>}
              </ul>
              <div className="mono-sm" style={{textAlign:"center", color: selected === p.id ? "var(--accent)" : "var(--ink-faint)"}}>
                {selected === p.id ? "✓ SELECTED" : "SELECT"}
              </div>
            </div>
          ))}
        </div>

        {/* Payment method + buy */}
        <div style={{width:"100%", display:"flex", flexDirection:"column", gap:12}}>
          {PAYPAL_ENABLED && (
            <div className="upgrade-method-toggle">
              <button className={`upgrade-method-btn ${method==="card"?"active":""}`} onClick={()=>setMethod("card")}>
                <span>💳</span> Card
              </button>
              <button className={`upgrade-method-btn ${method==="paypal"?"active":""}`} onClick={()=>setMethod("paypal")}>
                <span>🅿</span> PayPal
              </button>
            </div>
          )}
          <button className="cta-primary" style={{width:"100%", justifyContent:"center"}}
            onClick={handleBuy} disabled={!!loading}>
            {loading ? "Redirecting…"
              : !signedIn ? "Sign in to acquire credits →"
              : (PAYPAL_ENABLED && method === "paypal") ? "Pay with PayPal →"
              : "Pay with Card →"}
          </button>
        </div>

        {/* Footer */}
        <div className="upgrade-footer">
          <span className="mono-sm" style={{color:"var(--ink-faint)"}}>
            Secure one-time payment via Stripe{PAYPAL_ENABLED ? " or PayPal" : ""} · Credits never expire · Charter §§I–VI remains
          </span>
          <button className="upgrade-dismiss" onClick={onClose}>
            Continue on public key (limited free examinations)
          </button>
        </div>

      </div>
    </div>
  );
}


/* ============================================================
   RESTORE SCREEN — Magic Link Re-Authentication
   ============================================================ */
function RestoreScreen({ onClose, onRequest }) {
  const [email, setEmail] = React.useState("");
  const [state, setState] = React.useState("idle"); // idle | loading | sent | error
  const [message, setMessage] = React.useState("");

  async function handleSubmit() {
    if (!email || !email.includes("@")) {
      setState("error");
      setMessage("Please enter a valid email address.");
      return;
    }
    setState("loading");
    const result = await onRequest(email.toLowerCase().trim());
    if (result.success) {
      setState("sent");
      setMessage(result.message);
    } else {
      setState("error");
      setMessage(result.message);
    }
  }

  function handleKey(e) { if (e.key === "Enter") handleSubmit(); }

  return (
    <div className="upgrade-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="upgrade-modal" style={{maxWidth:440, gap:20}}>

        {/* Sigil */}
        <div className="upgrade-sigil">
          <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
            <circle cx="30" cy="30" r="27" stroke="var(--accent)" strokeWidth="0.7" opacity="0.4"/>
            <circle cx="30" cy="30" r="18" stroke="var(--accent)" strokeWidth="0.5" opacity="0.3"/>
            {[0,60,120,180,240,300].map((deg,i)=>{
              const r=18,rad=deg*Math.PI/180;
              return <circle key={i} cx={30+Math.cos(rad)*r} cy={30+Math.sin(rad)*r} r="2" fill="var(--accent)" opacity="0.5"/>;
            })}
            <path d="M30 20 C32.5 24 34 27 34 30 C34 33.5 32.2 36 30 36 C27.8 36 26 33.5 26 30 C26 27 27.5 24 30 20Z" fill="var(--ink)" opacity="0.85"/>
            <circle cx="30" cy="30" r="1.5" fill="var(--accent)" opacity="0.6"/>
          </svg>
        </div>

        {/* Header */}
        <div className="upgrade-header">
          <div className="label" style={{marginBottom:8}}>ORDO · SEMEION · TOKEN RESTORE</div>
          <h2 className="upgrade-title" style={{fontSize:"clamp(22px,3vw,30px)"}}>
            Restore your designation.
          </h2>
          <p className="upgrade-subtitle">
            Enter your email to receive a magic link
          </p>
        </div>

        {state === "idle" || state === "error" || state === "loading" ? (
          <>
            <div className="upgrade-lore" style={{textAlign:"left", maxWidth:"100%"}}>
              <p style={{fontSize:14, lineHeight:1.65}}>
                Your Examiner designation is stored in your browser. If you've switched 
                devices or cleared your browser data, enter the email you used to pay 
                and we'll send a magic link to restore your access.
              </p>
            </div>

            {state === "error" && (
              <div className="error-banner" style={{margin:0, animation:"none"}}>
                <span className="error-banner-icon">⚠</span>
                <span className="error-banner-text">{message}</span>
              </div>
            )}

            <div style={{width:"100%", display:"flex", flexDirection:"column", gap:8}}>
              <div className="gate-input-label">
                <span className="gate-mono gate-mono--faint">REGISTERED EMAIL ADDRESS</span>
              </div>
              <div className={`gate-input-wrap ${state === "error" ? "gate-input-wrap--error" : ""}`}>
                <span className="gate-input-prefix" style={{fontFamily:"var(--mono)", fontSize:12}}>@</span>
                <input
                  className="gate-input"
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setState("idle"); }}
                  onKeyDown={handleKey}
                  placeholder="your@email.com"
                  autoComplete="email"
                  style={{letterSpacing:"0.05em", textAlign:"left"}}
                />
              </div>
            </div>

            <button
              className="cta-primary"
              style={{width:"100%", justifyContent:"center", opacity: state === "loading" ? 0.6 : 1}}
              onClick={handleSubmit}
              disabled={state === "loading"}>
              {state === "loading" ? "Sending restore link…" : "Send Magic Link →"}
            </button>
          </>
        ) : (
          /* Sent state */
          <div style={{textAlign:"center", display:"flex", flexDirection:"column", alignItems:"center", gap:16}}>
            <div style={{
              width:48, height:48, borderRadius:"50%",
              border:"1px solid var(--signal)",
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:24, color:"var(--signal)"
            }}>✓</div>
            <div>
              <div style={{fontFamily:"var(--mono)", fontSize:10, letterSpacing:"0.14em", color:"var(--signal)", textTransform:"uppercase", marginBottom:8}}>
                RESTORE LINK SENT
              </div>
              <p style={{fontFamily:"var(--serif)", fontSize:15, color:"var(--ink-dim)", fontStyle:"italic", lineHeight:1.6}}>
                Check your inbox at <strong style={{color:"var(--ink)", fontStyle:"normal"}}>{email}</strong>.
                The link expires in 15 minutes.
              </p>
            </div>
            <p style={{fontFamily:"var(--serif)", fontSize:13, color:"var(--ink-faint)", fontStyle:"italic"}}>
              Don't see it? Check your spam folder.
            </p>
          </div>
        )}

        <div className="upgrade-footer">
          <span className="mono-sm" style={{color:"var(--ink-faint)"}}>
            Only works with the email used at checkout · Link expires in 15 minutes
          </span>
          <button className="upgrade-dismiss" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// Update exports

/* ============================================================
   AUTH SCREEN — Sign In / Sign Up / Reset Password
   ============================================================ */
/* Set-new-password screen, shown when arriving from a recovery email link. */
function NewPasswordScreen({ onSubmit, onDone }) {
  const [pw, setPw] = React.useState("");
  const [pw2, setPw2] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [done, setDone] = React.useState(false);

  async function submit() {
    setError("");
    if (pw.length < 8) { setError("Passphrase must be at least 8 characters."); return; }
    if (pw !== pw2) { setError("The two passphrases do not match."); return; }
    setLoading(true);
    const r = await onSubmit(pw);
    setLoading(false);
    if (r.success) setDone(true);
    else setError(r.message || "Reset failed.");
  }

  return (
    <div className="upgrade-overlay">
      <div className="upgrade-modal" style={{ maxWidth: 420, gap: 18 }}>
        <div className="upgrade-header">
          <div className="label" style={{ marginBottom: 8 }}>ORDO · SEMEION · PASSPHRASE RESET</div>
          <h2 className="upgrade-title" style={{ fontSize: "clamp(22px,3vw,30px)" }}>
            {done ? "Passphrase set." : "Choose a new passphrase."}
          </h2>
          <p className="upgrade-subtitle">
            {done ? "Your access is restored. Sign in with your new passphrase."
                  : "Enter a new passphrase for your account. Minimum 8 characters."}
          </p>
        </div>

        {error && <div className="auth-error">{error}</div>}

        {!done ? (
          <>
            <input className="gate-input" type="password" placeholder="New passphrase"
              value={pw} onChange={e => setPw(e.target.value)} style={{ width: "100%", textAlign: "left" }} />
            <input className="gate-input" type="password" placeholder="Confirm passphrase"
              value={pw2} onChange={e => setPw2(e.target.value)} onKeyDown={e => e.key === "Enter" && submit()} style={{ width: "100%", textAlign: "left" }} />
            <button className="cta-primary" style={{ width: "100%", justifyContent: "center" }} onClick={submit} disabled={loading}>
              {loading ? "Setting…" : "Set passphrase →"}
            </button>
          </>
        ) : (
          <button className="cta-primary" style={{ width: "100%", justifyContent: "center" }} onClick={onDone}>
            Continue to sign in →
          </button>
        )}
      </div>
    </div>
  );
}

function AuthScreen({ mode, onModeChange, onSignIn, onSignUp, onReset, onClose }) {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");

  async function handleSubmit() {
    setError(""); setSuccess("");
    if (!email || !email.includes("@")) { setError("Valid email required."); return; }

    setLoading(true);
    try {
      if (mode === "signin") {
        if (!password) { setError("Password required."); setLoading(false); return; }
        const data = await onSignIn(email, password);
        if (!data.success) setError(data.error || "Sign in failed.");
      } else if (mode === "signup") {
        if (!password || password.length < 8) { setError("Password must be 8+ characters."); setLoading(false); return; }
        const data = await onSignUp(email, password);
        if (data.success) {
          setSuccess(data.needsVerification
            ? "Account created. Check your email to verify before signing in."
            : "Account created. Welcome to the Order.");
        } else { setError(data.error || "Signup failed."); }
      } else if (mode === "reset") {
        const data = await onReset(email);
        setSuccess("If this email has an account, a reset link has been sent.");
      }
    } catch (e) { setError("Something went wrong. Try again."); }
    setLoading(false);
  }

  function handleKey(e) { if (e.key === "Enter") handleSubmit(); }

  const titles = { signin: "Sign In", signup: "Create Account", reset: "Reset Password" };
  const subtitles = {
    signin: "Access your account and credits across all devices.",
    signup: "Create a free account — 5 examination credits issued on registration, and your archive kept across devices.",
    reset: "Enter your email and we'll send a link to reset your passphrase.",
  };

  return (
    <div className="upgrade-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="upgrade-modal" style={{maxWidth:420, gap:20}}>

        {/* Sigil */}
        <div className="upgrade-sigil">
          <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
            <circle cx="28" cy="28" r="25" stroke="var(--accent)" strokeWidth="0.7" opacity="0.4"/>
            <circle cx="28" cy="28" r="16" stroke="var(--accent)" strokeWidth="0.5" opacity="0.3"/>
            {[0,60,120,180,240,300].map((deg,i)=>{
              const r=16,rad=deg*Math.PI/180;
              return <circle key={i} cx={28+Math.cos(rad)*r} cy={28+Math.sin(rad)*r} r="1.8" fill="var(--accent)" opacity="0.5"/>;
            })}
            <path d="M28 18 C30.5 22 32 25 32 28 C32 31.5 30.2 34 28 34 C25.8 34 24 31.5 24 28 C24 25 25.5 22 28 18Z" fill="var(--ink)" opacity="0.85"/>
          </svg>
        </div>

        {/* Header */}
        <div className="upgrade-header">
          <div className="label" style={{marginBottom:8}}>ORDO · SEMEION · FIELD INSTRUMENT №01</div>
          <h2 className="upgrade-title" style={{fontSize:"clamp(22px,3vw,30px)"}}>{titles[mode]}</h2>
          <p className="upgrade-subtitle">{subtitles[mode]}</p>
        </div>

        {/* Error / Success */}
        {error && (
          <div className="error-banner" style={{margin:0,width:"100%"}}>
            <span className="error-banner-icon">⚠</span>
            <span className="error-banner-text">{error}</span>
          </div>
        )}
        {success && (
          <div style={{padding:"12px 16px",border:"1px solid var(--signal)",background:"rgba(107,138,122,0.08)",width:"100%"}}>
            <span style={{fontFamily:"var(--mono)",fontSize:10,letterSpacing:"0.1em",color:"var(--signal)",textTransform:"uppercase"}}>
              ✓ {success}
            </span>
          </div>
        )}

        {/* Form */}
        {!success && (
          <div style={{width:"100%",display:"flex",flexDirection:"column",gap:12}}>
            <div className="gate-input-wrap">
              <span className="gate-input-prefix" style={{fontFamily:"var(--mono)",fontSize:12}}>@</span>
              <input className="gate-input" type="email" value={email}
                onChange={e => setEmail(e.target.value)} onKeyDown={handleKey}
                placeholder="your@email.com" autoComplete="email"
                style={{letterSpacing:"0.05em",textAlign:"left"}}/>
            </div>
            {mode !== "reset" && (
              <div className="gate-input-wrap">
                <span className="gate-input-prefix" style={{fontFamily:"var(--mono)",fontSize:12}}>§</span>
                <input className="gate-input" type="password" value={password}
                  onChange={e => setPassword(e.target.value)} onKeyDown={handleKey}
                  placeholder={mode === "signup" ? "8+ characters" : "password"}
                  autoComplete={mode === "signup" ? "new-password" : "current-password"}
                  style={{letterSpacing:"0.2em",textAlign:"left"}}/>
              </div>
            )}
            <button className="cta-primary"
              style={{width:"100%",justifyContent:"center",opacity:loading?0.6:1}}
              onClick={handleSubmit} disabled={loading}>
              {loading ? "Please wait…"
                : mode === "signin" ? "Sign In →"
                : mode === "signup" ? "Create Account →"
                : "Send Reset Link →"}
            </button>
          </div>
        )}

        {/* Mode switcher */}
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:8,width:"100%",borderTop:"1px solid var(--rule)",paddingTop:16}}>
          {mode === "signin" && (
            <>
              <button className="upgrade-dismiss" onClick={() => { onModeChange("signup"); setError(""); setSuccess(""); }}>
                No account? Create one
              </button>
              <button className="upgrade-dismiss" onClick={() => { onModeChange("reset"); setError(""); setSuccess(""); }}>
                Forgot password?
              </button>
            </>
          )}
          {mode === "signup" && (
            <button className="upgrade-dismiss" onClick={() => { onModeChange("signin"); setError(""); setSuccess(""); }}>
              Already have an account? Sign in
            </button>
          )}
          {mode === "reset" && (
            <button className="upgrade-dismiss" onClick={() => { onModeChange("signin"); setError(""); setSuccess(""); }}>
              Back to sign in
            </button>
          )}
          <button className="upgrade-dismiss" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   ACCOUNT SCREEN — Profile, designation status, sign out
   ============================================================ */
function AccountScreen({ account, credits = 0, onSignOut, onClose, onUpgrade,
                        onFetchProfile, onFetchPurchases, onExportData, onDeleteAccount }) {
  const [loading, setLoading] = React.useState(false);
  const [tab, setTab] = React.useState("overview"); // overview | history | data
  const [profile, setProfile] = React.useState(null);
  const [purchases, setPurchases] = React.useState(null);
  const [exporting, setExporting] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [deleteEmail, setDeleteEmail] = React.useState("");
  const [deleting, setDeleting] = React.useState(false);
  const [msg, setMsg] = React.useState("");
  const isExaminer = credits > 0;

  React.useEffect(() => {
    if (onFetchProfile) onFetchProfile().then(p => p && setProfile(p.profile || p));
  }, []);
  React.useEffect(() => {
    if (tab === "history" && purchases === null && onFetchPurchases) {
      onFetchPurchases().then(setPurchases);
    }
  }, [tab]);

  async function handleSignOut() { setLoading(true); await onSignOut(); setLoading(false); }
  async function handleExport() {
    setExporting(true); setMsg("");
    const r = await onExportData();
    setMsg(r.success ? "Data exported — check your downloads." : (r.error || "Export failed."));
    setExporting(false);
  }
  async function handleDelete() {
    setDeleting(true); setMsg("");
    const r = await onDeleteAccount(deleteEmail);
    if (r.success) { await onSignOut(); }
    else { setMsg(r.error || "Delete failed."); setDeleting(false); }
  }

  const since = profile?.created_at ? new Date(profile.created_at).toLocaleDateString("en-GB",{month:"short",year:"numeric"}) : "—";
  const lastAt = profile?.last_exam_at ? new Date(profile.last_exam_at).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"2-digit"}) : "—";

  return (
    <div className="upgrade-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="upgrade-modal account-modal" style={{maxWidth:460, gap:18}}>

        <div className="upgrade-sigil">
          <div style={{
            width:52,height:52,borderRadius:"50%",
            border:`1px solid ${isExaminer?"var(--signal)":"var(--rule)"}`,
            display:"flex",alignItems:"center",justifyContent:"center",
            fontSize:22, color: isExaminer?"var(--signal)":"var(--ink-faint)"
          }}>{isExaminer ? "✓" : "○"}</div>
        </div>

        <div className="upgrade-header">
          <div className="label" style={{marginBottom:6}}>EXAMINER · ORDO SEMEION</div>
          <h2 className="upgrade-title" style={{fontSize:22, wordBreak:"break-all"}}>{account?.email}</h2>
        </div>

        {/* Tabs */}
        <div className="account-tabs">
          {["overview","history","data"].map(t => (
            <button key={t} className={`account-tab ${tab===t?"account-tab--active":""}`} onClick={()=>{setTab(t);setMsg("");}}>
              {t === "overview" ? "Overview" : t === "history" ? "Purchases" : "Data & Privacy"}
            </button>
          ))}
        </div>

        {msg && <div className="account-msg">{msg}</div>}

        {/* OVERVIEW */}
        {tab === "overview" && (
          <>
            <div className="account-credit-card" style={{borderColor: isExaminer?"var(--signal)":"var(--rule)"}}>
              {profile?.tier && (
                <div className="account-tier-badge">◈ {profile.tier} · Designation</div>
              )}
              <div style={{fontFamily:"var(--mono)",fontSize:10,letterSpacing:"0.14em",textTransform:"uppercase",color:isExaminer?"var(--signal)":"var(--ink-faint)"}}>
                {isExaminer ? `✓ ${credits} EXAMINATION CREDIT${credits===1?"":"S"}` : "○ NO CREDITS"}
              </div>
              <p style={{fontFamily:"var(--serif)",fontSize:14,color:"var(--ink-dim)",fontStyle:"italic",margin:0}}>
                {isExaminer ? "Credits never expire · PDF export · Persistent archive · Shareable links"
                           : "No credits remaining. Acquire credits to continue examining."}
              </p>
              {credits > 0 && credits <= 3 && (
                <p style={{fontFamily:"var(--mono)",fontSize:9,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--warn)",margin:0}}>
                  ⚠ Running low — {credits} left
                </p>
              )}
              <button className="cta-primary" style={{width:"100%",justifyContent:"center",marginTop:4}} onClick={onUpgrade}>
                {isExaminer ? "Add more credits →" : "Acquire credits →"}
              </button>
            </div>

            <div className="account-stats">
              <div className="account-stat"><span className="account-stat-val">{profile?.exam_count ?? "—"}</span><span className="account-stat-label">Examinations Run</span></div>
              <div className="account-stat"><span className="account-stat-val">{profile?.saved_count ?? "—"}</span><span className="account-stat-label">Saved to Archive</span></div>
              <div className="account-stat"><span className="account-stat-val" style={{fontSize:15}}>{lastAt}</span><span className="account-stat-label">Last Examination</span></div>
              <div className="account-stat"><span className="account-stat-val" style={{fontSize:15}}>{since}</span><span className="account-stat-label">Member Since</span></div>
            </div>
          </>
        )}

        {/* PURCHASES */}
        {tab === "history" && (
          <div className="account-history">
            {purchases === null && <p className="account-empty">Loading…</p>}
            {purchases !== null && purchases.length === 0 && <p className="account-empty">No purchases yet.</p>}
            {purchases !== null && purchases.map((p,i) => (
              <div key={i} className="account-purchase">
                <div>
                  <div className="account-purchase-pack">{p.credits} credits</div>
                  <div className="account-purchase-meta">{new Date(p.created_at).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})} · {p.provider}</div>
                </div>
                <div className="account-purchase-amt">{p.currency || "USD"} {Number(p.amount).toFixed(2)}</div>
              </div>
            ))}
          </div>
        )}

        {/* DATA & PRIVACY */}
        {tab === "data" && (
          <div className="account-data">
            <p className="account-data-note">
              Saved examinations — including their specimen text — are stored to your account.
              Unsaved audits are never retained. You can export everything we hold, or erase it all.
            </p>
            <button className="upgrade-dismiss account-data-btn" onClick={handleExport} disabled={exporting}>
              {exporting ? "Preparing…" : "⬇ Export all my data (JSON)"}
            </button>

            {!confirmDelete ? (
              <button className="upgrade-dismiss account-data-btn account-data-btn--danger" onClick={()=>{setConfirmDelete(true);setMsg("");}}>
                ✕ Delete my account
              </button>
            ) : (
              <div className="account-delete-confirm">
                <p>This permanently erases your account, all saved examinations, and their specimen text. <strong>This cannot be undone.</strong></p>
                <p style={{marginTop:8}}>Type your email to confirm:</p>
                <input className="gate-input" style={{textAlign:"left",width:"100%",marginTop:6}}
                  value={deleteEmail} onChange={e=>setDeleteEmail(e.target.value)} placeholder={account?.email}/>
                <div style={{display:"flex",gap:8,marginTop:10}}>
                  <button className="upgrade-dismiss account-data-btn--danger" style={{flex:1}}
                    onClick={handleDelete} disabled={deleting || deleteEmail.toLowerCase().trim() !== (account?.email||"").toLowerCase()}>
                    {deleting ? "Deleting…" : "Permanently delete"}
                  </button>
                  <button className="upgrade-dismiss" style={{flex:1}} onClick={()=>{setConfirmDelete(false);setDeleteEmail("");}}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer actions */}
        <div style={{width:"100%",display:"flex",flexDirection:"column",gap:8,borderTop:"1px solid var(--rule)",paddingTop:16}}>
          <button className="upgrade-dismiss" onClick={handleSignOut} disabled={loading}>{loading ? "Signing out…" : "Sign out"}</button>
          <button className="upgrade-dismiss" onClick={onClose}>Close</button>
        </div>

      </div>
    </div>
  );
}

// Final exports
Object.assign(window, {
  Bench, Intake, Examination, Findings, Verdict, Archive,
  UpgradeScreen, RestoreScreen, AuthScreen, NewPasswordScreen, AccountScreen
});

/* ============================================================
   SHARED EXAMINATION VIEWER
   Full-screen overlay showing a shared examination (no auth)
   ============================================================ */
function SharedExaminationViewer({ entry, onClose, go }) {
  if (!entry) return null;
  const d = entry.report;
  const gc = entry.grade?.charAt(0) === "A" ? "var(--signal)"
    : entry.grade?.charAt(0) === "B" ? "var(--accent)"
    : entry.grade?.charAt(0) === "C" ? "var(--accent)"
    : "var(--warn)";
  const dqColor = parseFloat(d.drift_quotient) >= 0.7 ? "var(--warn)"
    : parseFloat(d.drift_quotient) >= 0.4 ? "var(--accent)"
    : "var(--signal)";

  const PRINCIPLES = [
    { k:"transparency",    n:"I",   name:"Transparency" },
    { k:"consent",         n:"II",  name:"Consent" },
    { k:"harm_reduction",  n:"III", name:"Harm Reduction" },
    { k:"power_awareness", n:"IV",  name:"Power Awareness" },
    { k:"empirical",       n:"V",   name:"Empirical" },
    { k:"cultural",        n:"VI",  name:"Cultural" },
  ];

  return (
    <div className="shared-overlay">
      {/* Header */}
      <div className="shared-header">
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
            <circle cx="16" cy="16" r="14.5" stroke="var(--accent)" strokeWidth="0.8" opacity="0.5"/>
            <path d="M16 10.5 C18 13 19.5 15 19.5 17 C19.5 19.5 17.9 21.5 16 21.5 C14.1 21.5 12.5 19.5 12.5 17 C12.5 15 14 13 16 10.5Z" fill="var(--ink)" opacity="0.9"/>
          </svg>
          <span style={{fontFamily:"var(--serif)",fontSize:15,letterSpacing:"0.1em",color:"var(--ink)"}}>SPECVLVM <em style={{color:"var(--accent)",fontStyle:"normal"}}>·</em> Auditus</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <span className="mono-sm" style={{color:"var(--ink-faint)"}}>SHARED EXAMINATION · {entry.shareViews} views</span>
          <button className="shared-close-btn" onClick={onClose}>✕ CLOSE</button>
        </div>
      </div>

      {/* Body */}
      <div className="shared-body">
        <div className="shared-content">

          {/* Title + grade */}
          <div className="shared-title-block">
            <div style={{flex:1}}>
              <div className="label" style={{marginBottom:8}}>
                {entry.ts} · {entry.input?.medium}
                {d._sharedByTier && <span style={{color:"var(--accent)"}}> · Examined by a {d._sharedByTier}</span>}
              </div>
              <h1 className="shared-title">{d.title}</h1>
              <p style={{fontFamily:"var(--serif)",fontSize:17,color:"var(--ink-dim)",fontStyle:"italic",marginTop:12,lineHeight:1.65}}>
                {d.synopsis}
              </p>
            </div>
            <div className="shared-grade-block" style={{borderColor:gc}}>
              <span style={{fontFamily:"var(--serif)",fontSize:48,fontWeight:300,color:gc,lineHeight:1}}>{entry.grade}</span>
              <span className="mono-sm" style={{color:"var(--ink-faint)",marginTop:4}}>CHARTER GRADE</span>
            </div>
          </div>

          {/* DQ bar */}
          <div className="verdict-banner" style={{borderColor:dqColor,marginBottom:24}}>
            <div style={{flex:1}}>
              <div className="verdict-dq-row">
                <span className="verdict-dq-label">DRIFT QUOTIENT §VI</span>
                <span className="verdict-dq-value" style={{color:dqColor}}>
                  {typeof d.drift_quotient === "number" ? d.drift_quotient.toFixed(2) : "—"}
                </span>
                <div className="verdict-dq-bar">
                  <div className="verdict-dq-fill" style={{
                    width:`${Math.round((d.drift_quotient||0)*100)}%`,
                    background:dqColor
                  }}/>
                </div>
              </div>
              <div style={{marginTop:12}}>
                <h3 style={{fontFamily:"var(--serif)",fontSize:18,fontStyle:"italic",color:"var(--ink)",marginBottom:8}}>{d.verdict_headline}</h3>
                <p style={{fontFamily:"var(--serif)",fontSize:14,color:"var(--ink-dim)",lineHeight:1.6}}>{d.verdict_blurb}</p>
              </div>
            </div>
          </div>

          {/* Scorecard */}
          {d.scorecard && (
            <div style={{marginBottom:24}}>
              <div className="label" style={{marginBottom:12}}>Charter Scorecard · §§I–VI</div>
              <div className="scorecard" style={{display:"flex",flexDirection:"column",gap:0,border:"1px solid var(--rule)"}}>
                {PRINCIPLES.map(p => {
                  const s = d.scorecard[p.k] || { score: 5, finding: "—" };
                  const col = parseFloat(s.score) >= 7 ? "var(--signal)" : parseFloat(s.score) >= 4 ? "var(--accent)" : "var(--warn)";
                  return (
                    <div key={p.k} style={{display:"grid",gridTemplateColumns:"28px 80px 1fr 40px",alignItems:"center",gap:"12px",padding:"10px 16px",borderBottom:"1px solid var(--rule-soft)"}}>
                      <span style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--accent)"}}>{p.n}</span>
                      <span style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--ink-dim)",textTransform:"uppercase",letterSpacing:"0.05em"}}>{p.name}</span>
                      <span style={{fontFamily:"var(--serif)",fontSize:13,color:"var(--ink-dim)",fontStyle:"italic"}}>{s.finding}</span>
                      <span style={{fontFamily:"var(--mono)",fontSize:14,color:col,textAlign:"right"}}>{s.score}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* CTA to examine */}
          <div style={{padding:"24px",border:"1px solid var(--rule)",background:"var(--bg-elev)",textAlign:"center"}}>
            <div className="label" style={{marginBottom:12}}>RUN YOUR OWN EXAMINATION</div>
            <p style={{fontFamily:"var(--serif)",fontSize:15,color:"var(--ink-dim)",fontStyle:"italic",marginBottom:16,lineHeight:1.6}}>
              The bench is open. Place a specimen — advertisement, speech, manifesto — and receive a full semiotic examination under Charter §§I–VI.
            </p>
            <button className="cta-primary" onClick={onClose}>
              Enter the Bench → (Signal key required)
            </button>
            <div style={{marginTop:10,fontFamily:"var(--mono)",fontSize:8,color:"var(--ink-faint)",letterSpacing:"0.1em",textTransform:"uppercase"}}>
              Nothing retained unless you save it · Ordo Semeion · MMXXVI
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

/* ============================================================
   THE LEDGER — Examination Compare (Instrument №04)
   Side-by-side cross-examination of two saved audits.
   ============================================================ */
function CompareViewer({ a, b, onClose }) {
  if (!a || !b) return null;
  const ra = a.report || {}, rb = b.report || {};
  const PRINCIPLES = [
    { k:"transparency", n:"I", name:"Transparency" },
    { k:"consent", n:"II", name:"Consent" },
    { k:"harm_reduction", n:"III", name:"Harm Reduction" },
    { k:"power_awareness", n:"IV", name:"Power Awareness" },
    { k:"empirical", n:"V", name:"Empirical" },
    { k:"cultural", n:"VI", name:"Cultural" },
  ];
  const dqa = parseFloat(ra.drift_quotient) || 0;
  const dqb = parseFloat(rb.drift_quotient) || 0;
  const meanA = (() => { const s = PRINCIPLES.map(p=>parseInt(ra.scorecard?.[p.k]?.score,10)).filter(n=>!isNaN(n)); return s.length? s.reduce((x,y)=>x+y,0)/s.length : null; })();
  const meanB = (() => { const s = PRINCIPLES.map(p=>parseInt(rb.scorecard?.[p.k]?.score,10)).filter(n=>!isNaN(n)); return s.length? s.reduce((x,y)=>x+y,0)/s.length : null; })();
  const col = v => v >= 0.7 ? "var(--warn)" : v >= 0.4 ? "var(--accent)" : "var(--signal)";
  const scol = v => v >= 7 ? "var(--signal)" : v >= 4 ? "var(--accent)" : "var(--warn)";

  function MetricRow({ label, va, vb, fmt = x => x, color }) {
    return (
      <div className="cmp-row">
        <div className="cmp-cell" style={color ? { color: color(va) } : null}>{fmt(va)}</div>
        <div className="cmp-key">{label}</div>
        <div className="cmp-cell" style={color ? { color: color(vb) } : null}>{fmt(vb)}</div>
      </div>
    );
  }

  return (
    <div className="shared-overlay">
      <div className="shared-header">
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontFamily:"var(--serif)",fontSize:15,letterSpacing:"0.1em",color:"var(--ink)"}}>
            THE LEDGER <em style={{color:"var(--accent)",fontStyle:"normal"}}>·</em> Cross-Examination
          </span>
        </div>
        <button className="shared-close-btn" onClick={onClose}>✕ CLOSE</button>
      </div>

      <div className="shared-body">
        <div className="shared-content" style={{maxWidth:900}}>
          <div className="label" style={{marginBottom:18, textAlign:"center"}}>Instrument №04 · Two specimens examined against each other</div>

          {/* Titles */}
          <div className="cmp-row cmp-row--titles">
            <div className="cmp-title-cell">
              <div className="cmp-grade" style={{color:scol(meanA ?? 0)}}>{a.grade}</div>
              <h3>{a.title}</h3>
              <div className="cmp-sub">{a.input?.medium} · {a.ts}</div>
            </div>
            <div className="cmp-vs">vs</div>
            <div className="cmp-title-cell">
              <div className="cmp-grade" style={{color:scol(meanB ?? 0)}}>{b.grade}</div>
              <h3>{b.title}</h3>
              <div className="cmp-sub">{b.input?.medium} · {b.ts}</div>
            </div>
          </div>

          {/* Headline metrics */}
          <div className="cmp-block">
            <MetricRow label="Drift §VI" va={dqa} vb={dqb} fmt={x=>x.toFixed(2)} color={col} />
            <MetricRow label="Intensity" va={ra.intensity ?? "—"} vb={rb.intensity ?? "—"} fmt={x=>`${x}/10`} />
            <MetricRow label="Asymmetry" va={ra.asymmetry || "—"} vb={rb.asymmetry || "—"} />
            <MetricRow label="Signal Integrity" va={ra.signal_integrity || "—"} vb={rb.signal_integrity || "—"} />
            <MetricRow label="Mean Charter" va={meanA ?? 0} vb={meanB ?? 0} fmt={x=>typeof x==="number"?x.toFixed(1):x} color={scol} />
          </div>

          {/* Charter scorecard side by side */}
          <div className="label" style={{margin:"24px 0 12px"}}>Charter Scorecard · §§I–VI</div>
          <div className="cmp-block">
            {PRINCIPLES.map(p => {
              const sa = parseInt(ra.scorecard?.[p.k]?.score, 10);
              const sb = parseInt(rb.scorecard?.[p.k]?.score, 10);
              return (
                <div className="cmp-row" key={p.k}>
                  <div className="cmp-cell" style={{color: isNaN(sa)?"var(--ink-faint)":scol(sa)}}>{isNaN(sa)?"—":sa}</div>
                  <div className="cmp-key">{p.n} · {p.name}</div>
                  <div className="cmp-cell" style={{color: isNaN(sb)?"var(--ink-faint)":scol(sb)}}>{isNaN(sb)?"—":sb}</div>
                </div>
              );
            })}
          </div>

          {/* Synopses */}
          <div className="cmp-synopsis-grid">
            <div className="cmp-synopsis">
              <div className="label" style={{marginBottom:8}}>Synopsis · {a.title}</div>
              <p>{ra.synopsis}</p>
            </div>
            <div className="cmp-synopsis">
              <div className="label" style={{marginBottom:8}}>Synopsis · {b.title}</div>
              <p>{rb.synopsis}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   BATCH SCREEN — progress + results of a batch examination
   ============================================================ */
function BatchScreen({ batch, go, onOpen, onCompare }) {
  if (!batch) { return (
    <div className="archive" style={{textAlign:"center"}}>
      <p className="empty-hint">No batch in progress.</p>
      <button className="cta-primary" onClick={() => go("intake")} style={{marginTop:12}}>To Intake →</button>
    </div>
  ); }

  const { total, done, results = [], running, failed = 0, stopped } = batch;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const sorted = [...results].sort((a,b) => (parseFloat(b.report?.drift_quotient)||0) - (parseFloat(a.report?.drift_quotient)||0));

  return (
    <div className="archive">
      <header className="archive-head">
        <div>
          <div className="label" style={{marginBottom:8}}>§ 02b · Batch Examination · The Ledger</div>
          <h1>{running ? "Examining the batch…" : "Batch complete"}</h1>
          <div className="count">
            {done} of {total} examined{failed ? ` · ${failed} failed` : ""}{stopped ? " · stopped (out of credits)" : ""}
          </div>
        </div>
      </header>

      {/* Progress bar */}
      {running && (
        <div className="batch-progress">
          <div className="batch-progress-track"><div className="batch-progress-fill" style={{width:`${pct}%`}}/></div>
          <div className="batch-progress-label">{pct}% · specimen {Math.min(done+1,total)} of {total}</div>
        </div>
      )}

      {/* Results */}
      {sorted.length > 0 && (
        <>
          <div className="label" style={{margin:"24px 0 12px"}}>Results · highest drift first</div>
          <div className="archive-cards">
            {sorted.map((a) => {
              const dq = parseFloat(a.report?.drift_quotient || 0);
              const gradeColor = a.verdictClass === "clean" ? "var(--signal)" : a.verdictClass === "mixed" ? "var(--accent)" : "var(--warn)";
              const dqColor = dq >= 0.7 ? "var(--warn)" : dq >= 0.4 ? "var(--accent)" : "var(--signal)";
              return (
                <div key={a.id} className="archive-card" onClick={() => onOpen(a)}>
                  <div className="archive-card-head">
                    <div className="archive-card-sigil"><GeneratedSigil seed={a.id} size={40} color="var(--accent)"/></div>
                    <div className="archive-card-id">
                      <span className="mono-sm" style={{color:"var(--ink-faint)"}}>{a.input?.medium}</span>
                      <span className="mono-sm" style={{color:dqColor}}>DQ {dq.toFixed(2)}</span>
                    </div>
                    <div className="archive-card-grade" style={{color:gradeColor}}>{a.grade}</div>
                  </div>
                  <div className="archive-card-title">{a.title}</div>
                  <div className="archive-card-verdict">
                    <span className={`verdict v-${a.verdictClass}`} style={{fontSize:"13px"}}>{a.verdictHeadline}</span>
                  </div>
                  <div className="archive-card-arrow">→</div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {!running && (
        <div className="action-row" style={{marginTop:40,paddingTop:24,borderTop:"1px solid var(--rule)"}}>
          {sorted.length >= 2 && (
            <button className="cta-primary" onClick={() => onCompare(sorted[0], sorted[sorted.length-1])}>
              ⊞ Compare highest vs lowest drift
            </button>
          )}
          <button className="cta-ghost" onClick={() => go("archive")}>View in Archive</button>
          <button className="cta-ghost" onClick={() => go("intake")}>New Batch</button>
        </div>
      )}
    </div>
  );
}

// Final exports
Object.assign(window, {
  Bench, Intake, Examination, Findings, Verdict, Archive,
  ShareCard, UpgradeScreen, RestoreScreen, AuthScreen, NewPasswordScreen, AccountScreen,
  SharedExaminationViewer, CompareViewer, BatchScreen
});
